import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import AnchoredPopover from './AnchoredPopover'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Send,
  RotateCw,
  X,
  Search,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Bot,
  Maximize2,
  ExternalLink,
  FileText,
  FileSpreadsheet,
  ClipboardList,
  Activity,
  Sparkles,
  History,
  Layers,
  RefreshCw,
  Check,
  XCircle,
  Clock,
} from 'lucide-react'
import PDFViewer from './PDFViewer'
import CostBreakdown from './CostBreakdown'
import RequestPanelTabs from './requestDetail/RequestPanelTabs'
import FieldsPanelHeader from './requestDetail/FieldsPanelHeader'
import ActivityLogPanel from './requestDetail/ActivityLogPanel'
import EditGate from './requestDetail/EditGate'
import { bcApi, requestsApi } from '../services/api'
import { bcEntityShortLabel } from '../utils/bcEntityLabels'
import { parseDocumentDate, toNumber, toQuantity } from '../utils/bcValues'
import { formatAuditLogsForActivity } from '../utils/auditLog'
import { formatDateTime, formatTimeAgo } from '../utils/dateUtils'
import useSegmentedTabIndicator from '../hooks/useSegmentedTabIndicator'

// Human-readable headline for the machine-readable codes the post pipeline returns.
const BC_ERROR_TITLES = {
  routing_error: 'Business Central routing could not be resolved',
  drift_blocked: 'Fields changed after approval — re-approval required',
  already_posted: 'This request was already posted to Business Central',
  needs_cleanup: 'A leftover document in Business Central must be removed first',
}

// Turn a post/retry response (or an axios error) into { title, details[] }.
// The API returns a short code in `error`, the explanation in `message`, and
// sometimes a list in `errors` — rendering only `error` showed a bare code
// like "routing_error" with no indication of what to fix.
function extractPostError(data, fallback) {
  const d = data || {}
  const code = typeof d.error === 'string' ? d.error : ''
  const title = BC_ERROR_TITLES[code] || d.message || code || fallback

  const details = []
  if (d.message && d.message !== title) details.push(d.message)
  for (const e of d.errors || []) {
    details.push(typeof e === 'string' ? e : (e?.message || e?.field || JSON.stringify(e)))
  }
  if (d.bc_error && d.bc_error !== d.message) details.push(d.bc_error)

  return { title, details }
}
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { isRequestLocked } from '../utils/requestStatus'
import { isReadOnlyUser } from '../utils/readerAccess'
import { useDisplayPreferences } from '../context/DisplayPreferencesContext'
import OpCoBadge from './opco/OpCoBadge'
import { MoveOpCoButton, UnassignedOpCoBanner } from './opco/MoveOpCoDialog'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInputType(field) {
  const ft = (field.field_type || '').toLowerCase()
  const bc = (field.bc_field || '').toLowerCase()
  const dn = (field.display_name || '').toLowerCase()
  if (ft === 'date' || bc.includes('date') || dn.includes('date')) return 'date'
  // Only a field declared numeric is numeric. Guessing from the name ("tax", "total",
  // "price") turned values like pricesIncludeTax = "true" and "£1,234.50" into number
  // inputs, which browsers blank because they are not valid numbers.
  if (ft === 'number' || ft === 'integer' || ft === 'currency' || ft === 'decimal') return 'number'
  return 'text'
}

function normaliseForInput(value, inputType) {
  if (value === null || value === undefined || value === '') return ''
  if (inputType === 'date') {
    const s = String(value).trim()
    // Parsed by hand, never through Date: that read 05/06/2026 as 6 May and, west of
    // UTC+0 in summer, moved midnight back a day, and the wrong date was then posted.
    return parseDocumentDate(s) || s
  }
  return String(value)
}

// Corporate-entity suffixes common enough that matching on them alone is
// meaningless — nearly every company name ends in "Pty Ltd"/"Inc"/etc., so
// without excluding these, searching e.g. "Harvey Norman Pty Ltd" scored
// every other "... Pty Ltd" company above zero and buried the real match in
// a wall of unrelated results that merely shared the suffix (same bug fixed
// in NSRequestView.jsx's identical fuzzyScore — this is BC's own copy of it).
const CORPORATE_SUFFIX_STOPWORDS = new Set([
  'pty', 'ltd', 'limited', 'inc', 'incorporated', 'llc', 'llp', 'co', 'corp',
  'corporation', 'plc', 'group', 'holdings', 'the',
])

function fuzzyScore(query, target) {
  if (!query || !target) return 0
  const q = query.toLowerCase().trim()
  const t = target.toLowerCase().trim()
  if (t === q) return 1
  if (t.startsWith(q)) return 0.9
  if (t.includes(q)) return 0.7
  const qw = q.split(/\s+/).filter(w => w && !CORPORATE_SUFFIX_STOPWORDS.has(w))
  const tw = t.split(/\s+/).filter(Boolean)
  if (qw.length === 0) return 0
  const matched = qw.filter(w => tw.some(tw2 => tw2.startsWith(w)))
  return 0.4 * (matched.length / qw.length)
}

// ─── LookupDropdown ──────────────────────────────────────────────────────────
// Generic smart dropdown for any BC lookup field (currencies, countries, etc.)
// Shows extracted value, validates against lookup list, sticky search bar,
// best matches at top. Supports loading state while BC data fetches.

function LookupDropdown({
  extractedValue,     // raw value from AI extraction
  options,            // [{ code, name }] from BC lookup
  value,              // current confirmed value (code)
  onChange,           // (code, name) => void
  placeholder,
  loading = false,    // true while BC lookup data is loading
  codeField = 'code', // which field is the "code/number"
  nameField = 'name', // which field is the display name
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(extractedValue || value || '')
  const [confirmed, setConfirmed] = useState(null) // { code, name }
  const dropRef = useRef(null)

  // A value that is already one of the options (a reload, or a value saved earlier) is
  // confirmed. Starting from "not matched" showed a warning on a valid value and left
  // the box text and the stored value able to drift apart.
  useEffect(() => {
    const v = String(value ?? '').trim().toLowerCase()
    const match = v
      ? options.find(o => String(o[codeField] ?? '').trim().toLowerCase() === v)
      : null
    setConfirmed(match ? { code: match[codeField], name: match[nameField] } : null)
  }, [options, value, codeField, nameField])

  // Filter and sort options by query
  const filtered = useMemo(() => {
    const q = (query || '').trim()
    const scored = options.map(o => ({
      ...o,
      _score: Math.max(
        fuzzyScore(q, o[nameField] || ''),
        fuzzyScore(q, o[codeField] || '')
      ),
    }))
    const top = scored.filter(s => s._score > 0).sort((a, b) => b._score - a._score)
    const bottom = scored.filter(s => s._score === 0)
    return { top, bottom }
  }, [query, options, codeField, nameField])

  function handleSelect(opt) {
    setConfirmed({ code: opt[codeField], name: opt[nameField] })
    setQuery(opt[nameField])
    setOpen(false)
    onChange(opt[codeField], opt[nameField])
  }

  function handleClear() {
    setConfirmed(null)
    // The box must show what will be posted: nothing. Restoring the extracted text left
    // a value on screen that the form no longer held.
    setQuery('')
    onChange('', '')
  }

  useEffect(() => {
    function onClickOutside(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const isValid = !!confirmed

  return (
    <div ref={dropRef} className="relative">
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 ${
        isValid
          ? 'border-green-400 dark:border-green-600'
          : (query || '').trim()
            ? 'border-amber-400 dark:border-amber-500'
            : 'border-gray-300 dark:border-gray-700'
      }`}>
        <input
          type="text"
          value={query}
          placeholder={loading ? 'Loading BC data…' : (placeholder || 'Select…')}
          disabled={loading}
          onFocus={() => !loading && setOpen(true)}
          onChange={e => {
            setQuery(e.target.value)
            // Emptying the box clears the stored value too: an unmatched extracted value
            // (say 'GBP' where BC keeps the local currency blank) could not be removed.
            if (confirmed || !e.target.value.trim()) { setConfirmed(null); onChange('', '') }
            setOpen(true)
          }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none min-w-0 disabled:opacity-50 disabled:cursor-wait"
        />
        {loading && <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin mr-2 flex-shrink-0" />}
        {!loading && (isValid || (query || '').trim()) && (
          <button onClick={handleClear} aria-label="Clear selection" className="p-1.5 mr-1 hover:text-red-500 text-gray-400 flex-shrink-0" title="Clear">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {!loading && (
          <button
            onClick={() => setOpen(o => !o)}
            aria-label="Show options"
            aria-expanded={open}
            className="p-1.5 mr-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0"
          >
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {isValid && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
          <span className="font-mono font-semibold">{confirmed.code}</span>
          {confirmed.name !== confirmed.code && (
            <><span className="text-gray-400">—</span><span>{confirmed.name}</span></>
          )}
        </p>
      )}
      {!isValid && !loading && (query || '').trim() && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Not matched — select from dropdown
        </p>
      )}

      {open && !loading && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-72 overflow-hidden flex flex-col">
          {/* Sticky search bar inside dropdown — same as VendorField */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); if (confirmed) { setConfirmed(null); onChange('', '') } }}
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
                placeholder={`Search ${placeholder || 'options'}…`}
                autoFocus
                className="flex-1 text-xs bg-transparent focus:outline-none"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {options.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 italic text-center">No options available from BC</p>
            )}

            {filtered.top.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-0.5">
                  <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide">Best matches</span>
                </div>
                {filtered.top.map(o => (
                  <button
                    key={o[codeField]}
                    onClick={() => handleSelect(o)}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between group"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 truncate">{o[nameField]}</span>
                    <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{o[codeField]}</span>
                  </button>
                ))}
              </>
            )}

            {filtered.bottom.length > 0 && (
              <>
                <div className={`px-3 pt-2 pb-0.5 ${filtered.top.length > 0 ? 'border-t border-gray-100 dark:border-gray-700 mt-1' : ''}`}>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">All</span>
                </div>
                {filtered.bottom.slice(0, 150).map(o => (
                  <button
                    key={o[codeField]}
                    onClick={() => handleSelect(o)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{o[nameField]}</span>
                    <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{o[codeField]}</span>
                  </button>
                ))}
              </>
            )}

            {options.length > 0 && filtered.top.length === 0 && filtered.bottom.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 italic text-center">No results for "{query}"</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── VendorField ──────────────────────────────────────────────────────────────
// Special case of LookupDropdown that also calls resolve-vendor API for
// fuzzy matching + caching vendor mappings. Falls back to the local lookup list.

function VendorField({ extractedValue, vendors, onVendorResolved, onVendorCleared, resolvedNumber = '', resolvedName = '' }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(extractedValue || '')
  const [resolved, setResolved] = useState(null)
  const dropRef = useRef(null)

  // The parent owns whether the vendor is confirmed (it can be confirmed automatically
  // from an exact match or an earlier confirmation); this box follows it.
  useEffect(() => {
    if (resolvedNumber) {
      setResolved({ number: resolvedNumber, name: resolvedName })
      setQuery(resolvedName || resolvedNumber)
    } else {
      setResolved(null)
    }
  }, [resolvedNumber, resolvedName])

  // Filter by query (no fuzzy matching) — just exact/contains matching
  const filteredVendors = useMemo(() => {
    const q = (query || '').trim().toLowerCase()
    if (!q) return vendors
    return vendors.filter(v =>
      v.code?.toLowerCase().includes(q) || v.name?.toLowerCase().includes(q)
    )
  }, [vendors, query])

  // No auto-resolve — just show extracted value. User must manually search/select.

  function doSearch() {
    // No API call — just toggle dropdown to show vendors list
    setOpen(true)
  }

  function handleSelect(candidate) {
    setResolved({ number: candidate.number, name: candidate.name })
    setQuery(candidate.name)
    setOpen(false)
    onVendorResolved(candidate.number, candidate.name)
  }

  function handleClear() {
    setResolved(null)
    setQuery(extractedValue || '')
    setOpen(false)
    onVendorCleared()
  }

  useEffect(() => {
    function handler(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isValid = !!resolved

  return (
    <div ref={dropRef} className="relative">
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 ${
        isValid ? 'border-green-400 dark:border-green-600'
          : (query || '').trim() ? 'border-amber-400 dark:border-amber-500'
          : 'border-gray-300 dark:border-gray-700'
      }`}>
        <input
          type="text" value={query} placeholder="Vendor name or number…"
          onFocus={() => { if (vendors.length > 0) setOpen(true) }}
          onChange={e => { setQuery(e.target.value); if (resolved) { setResolved(null); onVendorCleared() } }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none min-w-0"
        />
        {isValid && (
          <button onClick={handleClear} aria-label="Clear vendor" className="p-1.5 mr-1 hover:text-red-500 text-gray-400 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>
        )}
        {!isValid && (
          <button onClick={() => setOpen(o => !o)} aria-label="Show vendors" aria-expanded={open} className="p-1.5 mr-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0">
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {isValid && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
          <span className="font-mono font-semibold">{resolved.number}</span>
          <span className="text-gray-400">—</span><span>{resolved.name}</span>
        </p>
      )}
      {!isValid && (query || '').trim() && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Select from dropdown ▼
        </p>
      )}

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Filter vendors…" autoFocus
              className="w-full px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
          </div>

          {filteredVendors.length === 0 && (
            <p className="px-3 py-3 text-xs text-gray-400 italic text-center">No vendors match</p>
          )}

          {filteredVendors.length > 0 && (
            filteredVendors.slice(0, 100).map(v => (
              <button key={v.code} onClick={() => handleSelect({ number: v.code, name: v.name })}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between">
                <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{v.name}</span>
                <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{v.code}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

// BC fields that are the "vendor" — handled by VendorField with resolve-vendor API
const VENDOR_FIELD_KEYS = new Set(['vendorNumber', 'vendorName', 'buyFromVendorNo', 'buyFromVendorName'])
// Array-of-objects fields are rendered as the line items table, not as a text input
const ARRAY_FIELD_TYPES = new Set(['array_of_objects', 'table'])
// BC-computed fields that auto-populate after vendor selection — never extracted from PDF
// Hide these from the "required" indicator since they come from BC, not the document
const BC_AUTO_FIELDS = new Set([
  'payToName', 'payToVendorNumber', 'payToAddressLine1', 'payToAddressLine2',
  'payToCity', 'payToCountry', 'payToState', 'payToPostCode',
  'buyFromAddressLine1', 'buyFromAddressLine2', 'buyFromCity',
  'buyFromCountry', 'buyFromState', 'buyFromPostCode',
  'shipToName', 'shipToCode', 'shipToAddressLine1', 'shipToAddressLine2',
  'shipToCity', 'shipToPostCode',
  // Note: shipToCountry and shipToState are NOT auto-fields — they show as dropdowns
])

// ─── ItemSearchDropdown ───────────────────────────────────────────────────────
// Fuzzy-searchable dropdown over BC items with auto-resolution.
// Items from backend arrive as {code, name}. We normalise to {number, description}.
// Shows match status indicator like vendor field.
function ItemSearchDropdown({ value, extractedText, items, onChange, matchStatus, placeholder }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value || extractedText || '')
  const dropRef = useRef(null)

  // Follow the line's own value. Without the fallback, a line that moved up after a
  // removal kept the previous line's text in its box.
  useEffect(() => { setQuery(value || extractedText || '') }, [value, extractedText])

  // Typing an item number that exists but never opening the list used to leave the line
  // "unmatched" with no hint why. An exact code match is committed when the box loses focus.
  function commitExactMatch() {
    const q = query.trim().toLowerCase()
    if (!q || q === String(value || '').toLowerCase()) return
    const exact = items.find(it => String(it.number || '').toLowerCase() === q)
    if (exact) onChange({ number: exact.number, description: exact.description, unitCost: exact.unitCost })
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items.slice(0, 50)
    return items
      .map(it => ({
        ...it,
        _s: Math.max(fuzzyScore(q, it.number || ''), fuzzyScore(q, it.description || '')),
      }))
      .filter(it => it._s > 0)
      .sort((a, b) => b._s - a._s)
      .slice(0, 50)
  }, [query, items])

  useEffect(() => {
    function h(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // Border colour based on match status
  const borderClass = matchStatus === 'matched'
    ? 'border-green-400 dark:border-green-600'
    : matchStatus === 'unmatched'
    ? 'border-amber-400 dark:border-amber-500'
    : 'border-gray-300 dark:border-gray-700'

  return (
    <div ref={dropRef} className="relative w-full">
      <div className={`flex items-center border rounded bg-white dark:bg-gray-800 ${borderClass}`}>
        {matchStatus === 'matched' && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1.5 flex-shrink-0" />}
        {matchStatus === 'unmatched' && <AlertTriangle className="h-3 w-3 text-amber-500 ml-1.5 flex-shrink-0" />}
        <input
          type="text"
          value={query}
          placeholder={placeholder || 'Item no. or description…'}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(commitExactMatch, 150)}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          className="flex-1 px-2 py-1.5 text-xs bg-transparent focus:outline-none min-w-0"
        />
        <button onClick={() => setOpen(o => !o)} aria-label="Show items" aria-expanded={open} className="px-1.5 flex-shrink-0">
          <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-xl max-h-52 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400 italic">No items found</p>
          )}
          {filtered.map(it => (
            <button
              key={it.number}
              onClick={() => {
                setQuery(it.number)
                setOpen(false)
                onChange({ number: it.number, description: it.description, unitCost: it.unitCost })
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between"
            >
              <span className="text-xs text-gray-900 dark:text-gray-100 font-medium truncate">{it.description || it.number}</span>
              <span className="ml-2 text-[10px] text-gray-400 font-mono flex-shrink-0">{it.number}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── LineItemsTable ───────────────────────────────────────────────────────────
let lineKeySeq = 0
const newLineKey = () => `line-${++lineKeySeq}`
const blankLine = () => ({ _key: newLineKey(), lineObjectNumber: '', description: '', quantity: 1, directUnitCost: '', unitOfMeasureCode: '' })

function LineItemsTable({ lines, items, onChange }) {
  function update(idx, field, val) {
    const next = lines.map((l, i) => i === idx ? { ...l, [field]: val } : l)
    onChange(next)
  }

  function addLine() {
    onChange([...lines, blankLine()])
  }

  function removeLine(idx) {
    onChange(lines.filter((_, i) => i !== idx))
  }

  const lineTotal = lines.reduce((sum, l) => {
    const qty = toNumber(l.quantity, 0)
    const cost = toNumber(l.directUnitCost, 0)
    return sum + qty * cost
  }, 0)

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">

      {/* Scrollable rows */}
      <div className="overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800" style={{ maxHeight: '400px' }}>
        {lines.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-400">No line items yet — add one below</p>
          </div>
        )}

        {lines.map((line, idx) => (
          <div
            key={line._key ?? idx}
            className={`px-4 py-3 space-y-2 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}
          >
            {/* Row header: line number + remove */}
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Line {idx + 1}</span>
              <button
                onClick={() => removeLine(idx)}
                aria-label={`Remove line ${idx + 1}`}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>

            {/* Row 1: Item No + Description (full width each) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Item No.</label>
                <ItemSearchDropdown
                  value={line.lineObjectNumber}
                  extractedText={line._extractedText || ''}
                  items={items}
                  matchStatus={line.lineObjectNumber && line._matched ? 'matched' : (line._extractedText && !line._matched) ? 'unmatched' : null}
                  onChange={sel => {
                    const next = lines.map((l, i) => i === idx ? {
                      ...l,
                      lineObjectNumber: sel.number,
                      description: l.description || sel.description || '',
                      directUnitCost: l.directUnitCost || sel.unitCost || '',
                      _matched: true,
                    } : l)
                    onChange(next)
                  }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Description</label>
                <input
                  type="text"
                  value={line.description}
                  onChange={e => update(idx, 'description', e.target.value)}
                  placeholder="Item description"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
              </div>
            </div>

            {/* Row 2: Qty, Unit Cost, UOM */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Quantity</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={line.quantity}
                  onChange={e => update(idx, 'quantity', e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 text-right"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Unit Cost</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={line.directUnitCost}
                  onChange={e => update(idx, 'directUnitCost', e.target.value)}
                  placeholder="0.00"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 text-right"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">UOM</label>
                <input
                  type="text"
                  value={line.unitOfMeasureCode}
                  onChange={e => update(idx, 'unitOfMeasureCode', e.target.value)}
                  placeholder="PCS"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 uppercase"
                />
              </div>
            </div>

            {/* Line subtotal */}
            {toNumber(line.quantity, 0) > 0 && toNumber(line.directUnitCost, 0) > 0 && (
              <p className="text-right text-[11px] text-gray-400">
                Subtotal: <span className="font-medium text-gray-600 dark:text-gray-300">
                  {(toNumber(line.quantity, 0) * toNumber(line.directUnitCost, 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Footer: Add Line + total */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button
          onClick={addLine}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-indigo-400 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add Line
        </button>
        {lineTotal > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total:&nbsp;<span className="font-semibold text-gray-900 dark:text-gray-100">
              {lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </p>
        )}
      </div>
    </div>
  )
}

export default function BCRequestView({ request, showProcessingModal, processingMinimized, setProcessingMinimized, processingProgress = { percent: 0, message: '' }, processingLog = [], onOpCoMoved, onPosted }) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const { showConfidenceScore, showAiBadge, showColorCoding } = useDisplayPreferences()
  const isReader = isReadOnlyUser(authUser)
  const isEditable = !isReader && !isRequestLocked(request?.status)
  const notifyLocked = () => toast.info('Read-only', isReader ? 'You have read-only access' : 'This request is finalized and can no longer be edited')

  // Draggable split between Review Fields (left) and document viewer (right),
  // matching the normal extraction page's fields-left/document-right layout.
  // Value = % width of the left (fields) panel.
  const [leftPanelPct, setLeftPanelPct] = useState(50)
  // Toggle to hide the document viewer entirely, same as the normal extraction page.
  const [isDocViewerVisible, setIsDocViewerVisible] = useState(true)
  const [isDraggingDivider, setIsDraggingDivider] = useState(false)
  const mainContentRef = useRef(null)

  // Action bar state — Cancel opens the request-cancellation modal.
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  // Left-panel tabs: Fields / Activity / AI Insights (no Exports — not applicable to BC)
  const [leftPanelTab, setLeftPanelTab] = useState('fields')
  const [fieldSearchQuery, setFieldSearchQuery] = useState('')
  const [fieldsExtractionFilter, setFieldsExtractionFilter] = useState('all') // 'all', 'extracted', 'not_extracted'
  const [fieldsViewFilter, setFieldsViewFilter] = useState('all') // 'all', 'mandatory' or 'custom'
  const { tabRefs: fieldsViewTabRefs, indicatorStyle: fieldsViewIndicatorStyle } = useSegmentedTabIndicator(fieldsViewFilter, [leftPanelTab])
  const [activityLogs, setActivityLogs] = useState([])
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activityLoaded, setActivityLoaded] = useState(false)

  // Field to highlight in the PDF viewer (bounding_box + page_number), set by
  // clicking a field row — same "click to jump to source" behaviour as the
  // normal request page.
  const [highlightedField, setHighlightedField] = useState(null)

  // Alternatives popup — per-field, keyed by field id so only one is open at a time.
  const [openAlternativesFieldId, setOpenAlternativesFieldId] = useState(null)
  const [alternatives, setAlternatives] = useState([])
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)
  const alternativesPopupRef = useRef(null)

  // Audit history popup — per-field.
  const [openAuditFieldId, setOpenAuditFieldId] = useState(null)
  const [fieldAuditLogs, setFieldAuditLogs] = useState([])
  const [loadingFieldAudit, setLoadingFieldAudit] = useState(false)
  const auditPopupRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alternativesPopupRef.current && !alternativesPopupRef.current.contains(event.target)) {
        setOpenAlternativesFieldId(null)
      }
      if (auditPopupRef.current && !auditPopupRef.current.contains(event.target)) {
        setOpenAuditFieldId(null)
      }
    }
    if (openAlternativesFieldId != null || openAuditFieldId != null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openAlternativesFieldId, openAuditFieldId])

  const handleViewFieldInDocument = (field) => {
    const hasValue = !!(field?.value ?? '').toString().trim()
    if (!hasValue) {
      setHighlightedField(null)
      return
    }
    setHighlightedField({
      bounding_box: field.bounding_box,
      page_number: field.page_number,
      field_name: field.field_name,
      extracted_value: field.value,
    })
  }

  const handleToggleAlternatives = async (field) => {
    if (openAlternativesFieldId === field.id) {
      setOpenAlternativesFieldId(null)
      return
    }
    setOpenAuditFieldId(null)
    setOpenAlternativesFieldId(field.id)
    setLoadingAlternatives(true)
    try {
      const res = await requestsApi.getFieldAlternatives(request.id, field.field_name)
      setAlternatives(res.data.alternatives || [])
    } catch (err) {
      console.error('Error loading field alternatives:', err)
      setAlternatives([])
    } finally {
      setLoadingAlternatives(false)
    }
  }

  const handleSelectAlternative = (field, alt) => {
    const newValue = alt.field_value || alt.extracted_value || ''
    setFieldValues(prev => ({ ...prev, [field.id]: newValue }))
    setOpenAlternativesFieldId(null)
    if (alt.bounding_box || alt.page_number) {
      setHighlightedField({
        bounding_box: alt.bounding_box,
        page_number: alt.page_number,
        field_name: field.field_name,
        extracted_value: newValue,
      })
    }
  }

  const handleToggleFieldAudit = async (field) => {
    if (openAuditFieldId === field.id) {
      setOpenAuditFieldId(null)
      return
    }
    setOpenAlternativesFieldId(null)
    setOpenAuditFieldId(field.id)
    setLoadingFieldAudit(true)
    try {
      const res = await requestsApi.getFieldAuditLogs(request.id, field.id, { limit: 20 })
      setFieldAuditLogs(res.data.audit_logs || [])
    } catch (err) {
      console.error('Error loading field audit log:', err)
      setFieldAuditLogs([])
    } finally {
      setLoadingFieldAudit(false)
    }
  }

  const fetchActivityLogs = useCallback(async () => {
    if (!request?.id) return
    try {
      setLoadingActivity(true)
      const res = await requestsApi.getAuditLogs(request.id, { limit: 20 })
      setActivityLogs(formatAuditLogsForActivity(res.data.audit_logs || []))
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setActivityLogs([])
    } finally {
      setLoadingActivity(false)
      setActivityLoaded(true)
    }
  }, [request?.id])

  useEffect(() => {
    if (leftPanelTab === 'activity' && !activityLoaded) {
      fetchActivityLogs()
    }
  }, [leftPanelTab, activityLoaded, fetchActivityLogs])

  const handleDividerMouseDown = (e) => {
    e.preventDefault()
    setIsDraggingDivider(true)

    const onMouseMove = (moveEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      let pct = ((moveEvent.clientX - rect.left) / rect.width) * 100
      pct = Math.min(80, Math.max(20, pct))
      setLeftPanelPct(pct)
    }
    const onMouseUp = () => {
      setIsDraggingDivider(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // Normalise fields from backend. Keyed on the field VALUES rather than the request
  // object: the page polls while a document extracts and hands over a new object each
  // time, which recreated every derived list and restarted the validate and preflight
  // timers (two calls to Business Central every few seconds).
  const fieldsKey = JSON.stringify((request?.fields || []).map(f => [
    f.id, f.field_value ?? f.value ?? '', f.is_active, f.confidence ?? null, f.alternative_count ?? 0,
  ]))
  const fields = useMemo(() =>
    (request?.fields || []).filter(f => f.is_active !== false).map(f => ({
      ...f,
      value: f.value ?? f.field_value ?? '',
      display_name: f.display_name || f.template_field?.display_name || f.field_name || '',
      bc_field: f.bc_field ?? f.template_field?.bc_field ?? null,
      is_required: f.is_required || f.template_field?.is_required || false,
      field_type: f.field_type || f.template_field?.field_type || f.template_field?.data_type || 'text',
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [fieldsKey])

  // Header-only fields (exclude array_of_objects — those become line items)
  const headerFields = useMemo(() => fields.filter(f => !ARRAY_FIELD_TYPES.has(f.field_type)), [fields])

  // The vendor as printed on the document, and whether this document type has a vendor at
  // all. Defined here because the vendor auto-confirm effect below needs them.
  const primaryVendorValue = headerFields.find(f => VENDOR_FIELD_KEYS.has(f.bc_field))?.value ?? ''
  const isPurchaseEntityEarly = !request?.template_bc_entity_type || request.template_bc_entity_type.startsWith('purchase')

  // The single array_of_objects field that holds every line row. Line edits must be
  // written back into THIS field, since lines are not stored one-per-request-field.
  const lineItemsField = useMemo(() => fields.find(f =>
      ARRAY_FIELD_TYPES.has(f.field_type) ||
      ARRAY_FIELD_TYPES.has(f.template_field?.field_type) ||
      ARRAY_FIELD_TYPES.has(f.template_field?.data_type) ||
      // Fallback: any field whose value looks like a JSON array (for older templates without explicit type)
      (f.value && typeof f.value === 'string' && f.value.trimStart().startsWith('[') &&
        (f.field_name?.toLowerCase().includes('line') || f.field_name?.toLowerCase().includes('product') ||
         f.field_name?.toLowerCase().includes('item') || f.field_name === 'products' || f.field_name === 'line_items'))
    ), [fields])

  // The stored rows of the line-items field, as extracted. Edits are merged back into
  // these on posting so columns the grid does not show (discounts, tax, custom columns)
  // are not lost from the request.
  const rawLineRows = useMemo(() => {
    const v = lineItemsField?.value
    if (!v) return []
    try {
      const raw = typeof v === 'string' ? JSON.parse(v) : v
      return Array.isArray(raw) ? raw : []
    } catch { return [] }
  }, [lineItemsField])

  // Parse line items from AI-extracted array_of_objects fields
  const extractedLines = useMemo(() => {
    const tableField = lineItemsField
    if (!tableField?.value) return []
    try {
      const raw = typeof tableField.value === 'string' ? JSON.parse(tableField.value) : tableField.value
      if (!Array.isArray(raw)) return []
      return raw.map((rawRow, srcIdx) => {
        // Normalise keys: lowercase, trim, replace spaces/dots/dashes with underscores
        const row = Object.fromEntries(
          Object.entries(rawRow).map(([k, v]) => [k.toLowerCase().trim().replace(/[\s.-]+/g, '_'), v])
        )
        // Also keep original lowercase keys for exact matches like 'code'
        const rowOrig = Object.fromEntries(Object.entries(rawRow).map(([k, v]) => [k.toLowerCase().trim(), v]))

        // Helper: find first truthy value across multiple key variants
        const pick = (...keys) => {
          for (const k of keys) {
            if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k]
            if (rowOrig[k] !== undefined && rowOrig[k] !== null && rowOrig[k] !== '') return rowOrig[k]
          }
          return ''
        }

        const unitCost = toNumber(pick('unit_price', 'unitprice', 'price', 'unit_cost', 'directunitcost', 'cost', 'rate', 'unit_amount'), null)
        return {
          _key: newLineKey(),
          _srcIdx: srcIdx,
          lineObjectNumber: pick('item_code', 'code', 'number', 'item_no', 'item_no_', 'item_number', 'itemno', 'product_code', 'sku', 'lineobjectnumber', 'item_no.', 'part_number', 'partno', 'material_code'),
          description:      pick('description', 'item_description', 'product', 'name', 'desc', 'item_name', 'product_name', 'product_description', 'line_description'),
          // 'amount' is deliberately not a quantity alias: on most invoices it is the line
          // TOTAL, and reading it as a quantity posted 300 units for a £300 line.
          // Only a missing quantity becomes 1; a stated 0 stays 0 so the reviewer sees it.
          quantity:         toQuantity(pick('quantity', 'qty', 'ordered_qty', 'order_quantity', 'order_qty')),
          directUnitCost:   unitCost === null ? '' : unitCost,
          unitOfMeasureCode: pick('uom', 'unit', 'unit_of_measure', 'unitofmeasurecode', 'unit_of_measure_code', 'measure'),
        }
      })
    } catch { return [] }
  }, [lineItemsField])

  const [lineItems, setLineItems] = useState(() =>
    extractedLines.length > 0 ? extractedLines : [blankLine()]
  )

  // Sync lineItems when extractedLines changes (fields load async after mount)
  const extractedLinesLoaded = useRef(false)
  useEffect(() => {
    if (extractedLines.length > 0 && !extractedLinesLoaded.current) {
      extractedLinesLoaded.current = true
      setLineItems(extractedLines)
    }
  }, [extractedLines])

  const [fieldValues, setFieldValues] = useState(() => {
    const init = {}
    for (const f of headerFields) init[f.id] = normaliseForInput(f.value, getInputType(f))
    return init
  })

  // Re-sync fieldValues when fields load (request data may arrive after first render)
  const fieldsSynced = useRef(false)
  useEffect(() => {
    if (headerFields.length === 0 || fieldsSynced.current) return
    fieldsSynced.current = true
    setFieldValues(prev => {
      const next = { ...prev }
      let changed = false
      for (const f of headerFields) {
        const incoming = normaliseForInput(f.value, getInputType(f))
        if (incoming && !prev[f.id]) { next[f.id] = incoming; changed = true }
      }
      return changed ? next : prev
    })
  }, [headerFields])

  const handleCancelRequest = useCallback(async () => {
    if (!cancelReason.trim()) return
    setCancelling(true)
    try {
      await requestsApi.cancel(request.id, cancelReason)
      setShowCancelModal(false)
      navigate(-1)
    } catch {
      toast.error('Failed to cancel request')
    } finally {
      setCancelling(false)
    }
  }, [cancelReason, request.id, navigate, toast])

  // Average confidence across all header fields that have a confidence value —
  // shown in the top bar, same as the NetSuite and normal extraction pages.
  const avgConfidence = useMemo(() => {
    const withConf = headerFields.filter(f => f.confidence != null && f.confidence > 0)
    if (!withConf.length) return null
    return Math.min(100, Math.max(0, Math.round(
      (withConf.reduce((s, f) => s + (f.confidence || 0), 0) / withConf.length) * 100
    )))
  }, [headerFields])

  // ── BC Lookups (vendors, currencies, paymentTerms, etc.) ──
  const [lookups, setLookups] = useState({})       // { vendors: [...], currencies: [...], ... }
  const [lookupFields, setLookupFields] = useState({}) // { vendorNumber: { entity, code, name }, ... }
  const [lookupsLoading, setLookupsLoading] = useState(true)
  // Why the lists could not load, shown with a Retry. Failing silently left every field
  // as a plain text box and the vendor list empty, with no hint that BC was the problem.
  const [lookupsError, setLookupsError] = useState(null)
  const [lookupsReload, setLookupsReload] = useState(0)
  const describeError = (err, fallback) =>
    err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback

  // Phase 1: fetch lookup-config instantly (no BC calls) so dropdowns render immediately
  useEffect(() => {
    bcApi.getLookupConfig()
      .then(res => setLookupFields(res.data?.lookup_fields || {}))
      .catch(err => setLookupsError(describeError(err, 'Could not load the Business Central field configuration.')))
  }, [lookupsReload])

  // Phase 2: fetch the entity data itself (vendors, currencies, countries, ...).
  // Served from the local master data cache, scoped to this template's BC
  // connection — one call, no per-field round trips to BC.
  const [masterDataInfo, setMasterDataInfo] = useState(null)
  useEffect(() => {
    setLookupsLoading(true)
    bcApi.getLookups(request?.template_id)
      .then(res => {
        setLookupsError(null)
        const data = res.data || {}
        delete data.lookup_fields  // already loaded from lookup-config
        // Where these lists came from, and how fresh they are.
        setMasterDataInfo(data.master_data || null)
        delete data.master_data
        setLookups(data)
      })
      .catch(err => setLookupsError(describeError(err, 'Could not load the Business Central lists.')))
      .finally(() => setLookupsLoading(false))
  }, [request?.template_id, lookupsReload])

  // Helper: get lookup options for a given bc_field, or null if no lookup
  function getLookupOptions(bcField) {
    if (!bcField || !lookupFields[bcField]) return null
    const info = lookupFields[bcField]
    return lookups[info.entity] || []
  }

  // BC items list for line item search — normalise from {code, name} to {number, description}
  const bcItems = useMemo(() => {
    const raw = lookups.items || []
    return raw.map(it => ({
      number: it.code || it.number || '',
      description: it.name || it.description || it.displayName || '',
      unitCost: it.unitCost || it.unitPrice || '',
    }))
  }, [lookups])

  // Auto-resolve extracted line items against BC items (like vendor resolution)
  // Runs when bcItems load OR when extractedLines change
  const lineItemsInitialised = useRef(false)
  useEffect(() => {
    if (bcItems.length === 0) return // wait for items to load
    if (lineItemsInitialised.current) return // only run once
    lineItemsInitialised.current = true

    // If extractedLines has data, resolve each against BC items
    const linesToResolve = extractedLines.length > 0 ? extractedLines : lineItems
    if (!linesToResolve.some(l => l.description || l.lineObjectNumber)) return

    const resolved = linesToResolve.map(line => {
      // If already has a valid item number that matches BC, keep it
      if (line.lineObjectNumber) {
        const exact = bcItems.find(it =>
          it.number.toLowerCase() === line.lineObjectNumber.toLowerCase()
        )
        if (exact) return { ...line, _matched: true, _extractedText: line.lineObjectNumber }
      }

      // Try to fuzzy match by description or item code from extraction
      const searchText = (line.lineObjectNumber || line.description || '').toLowerCase()
      if (!searchText) return { ...line, _matched: false, _extractedText: '' }

      // Score all BC items
      const scored = bcItems
        .map(it => ({
          ...it,
          _s: Math.max(fuzzyScore(searchText, it.number), fuzzyScore(searchText, it.description)),
        }))
        .filter(it => it._s > 0)
        .sort((a, b) => b._s - a._s)

      // If top match score > 80%, auto-select it
      if (scored.length > 0 && scored[0]._s >= 0.8) {
        return {
          ...line,
          lineObjectNumber: scored[0].number,
          description: line.description || scored[0].description,
          _matched: true,
          _extractedText: searchText,
        }
      }

      // Otherwise mark as unmatched — user picks from dropdown
      return {
        ...line,
        _matched: false,
        _extractedText: searchText,
      }
    })

    setLineItems(resolved)
  }, [bcItems, extractedLines])

  // ── Vendor state (special handling) ──
  const [vendorResolved, setVendorResolved] = useState(false)
  const [vendorBcNumber, setVendorBcNumber] = useState('')
  const [vendorBcName, setVendorBcName] = useState('')

  function handleVendorResolved(number, name) {
    setVendorResolved(true)
    setVendorBcNumber(number)
    setVendorBcName(name)
    // Fill ALL vendor-related fields at once — number fields get the code, name fields get the name
    setFieldValues(prev => {
      const next = { ...prev }
      for (const f of headerFields) {
        if (!f.bc_field) continue
        if (f.bc_field === 'vendorNumber' || f.bc_field === 'buyFromVendorNo') next[f.id] = number
        if (f.bc_field === 'vendorName' || f.bc_field === 'buyFromVendorName') next[f.id] = name
      }
      return next
    })
  }

  // A vendor picked by hand is remembered (vendor_mappings), so the next document that
  // prints the same name is confirmed without anyone searching again.
  function handleVendorPicked(number, name) {
    handleVendorResolved(number, name)
    const printed = String(primaryVendorValue || '').trim()
    if (printed) {
      bcApi.confirmVendor({
        pdf_name: printed, bc_number: number, bc_name: name,
        template_id: request?.template_id, request_id: request?.id,
      }).catch(() => { /* remembering is best-effort; the pick itself already applied */ })
    }
  }

  function handleVendorCleared() {
    setVendorResolved(false)
    setVendorBcNumber('')
    setVendorBcName('')
    // Clear ALL vendor fields
    setFieldValues(prev => {
      const next = { ...prev }
      for (const f of headerFields) {
        if (VENDOR_FIELD_KEYS.has(f.bc_field)) next[f.id] = ''
      }
      return next
    })
  }

  // Confirm the vendor without a click when it is unambiguous: the printed text is a
  // vendor code, or the exact name of exactly one vendor, or a name a person confirmed
  // before. Previously every document started "Not confirmed", even when it was right.
  const vendorAutoTried = useRef(false)
  useEffect(() => {
    if (vendorAutoTried.current || vendorResolved || !isPurchaseEntityEarly) return
    const printed = String(primaryVendorValue || '').trim()
    const vendors = lookups.vendors || []
    if (!printed || vendors.length === 0) return
    vendorAutoTried.current = true
    const lower = printed.toLowerCase()
    const byCode = vendors.find(v => String(v.code ?? '').toLowerCase() === lower)
    const byName = vendors.filter(v => String(v.name ?? '').toLowerCase() === lower)
    const match = byCode || (byName.length === 1 ? byName[0] : null)
    if (match) {
      handleVendorResolved(match.code, match.name)
      return
    }
    bcApi.resolveVendor({ vendor_name: printed, template_id: request?.template_id, request_id: request?.id })
      .then(res => {
        const d = res.data || {}
        if (d.resolved && d.from_cache && d.bc_vendor_number) {
          handleVendorResolved(d.bc_vendor_number, d.bc_vendor_name || '')
        }
      })
      .catch(() => { /* the reviewer can still pick the vendor */ })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookups.vendors, primaryVendorValue, vendorResolved])

  // ── Server-side validation (debounced) ──
  const [validationErrors, setValidationErrors] = useState([])
  const [validationFailed, setValidationFailed] = useState(false)
  // Only the newest validation may write its result; a slow older reply used to land
  // after a newer one and show errors that had already been fixed.
  const validationSeq = useRef(0)
  const [validating, setValidating] = useState(false)
  const validationTimer = useRef(null)

  const runValidation = useCallback(() => {
    const seq = ++validationSeq.current
    setValidating(true)
    const payload = {}
    for (const f of headerFields) {
      if (f.bc_field) payload[f.bc_field] = fieldValues[f.id] ?? ''
    }
    if (vendorResolved && vendorBcNumber) payload['vendorNumber'] = vendorBcNumber
    // Include line items so server can validate item numbers, UOM codes etc.
    const lines = lineItems
      .filter(l => l.lineObjectNumber || l.description)
      .map(l => ({
        lineObjectNumber: l.lineObjectNumber || '',
        description: l.description || '',
        quantity: toNumber(l.quantity, 0),
        directUnitCost: toNumber(l.directUnitCost, 0),
        unitOfMeasureCode: l.unitOfMeasureCode || '',
      }))
    bcApi.validate({ request_id: request.id, template_id: request.template_id, payload, line_items: lines })
      .then(res => {
        if (seq !== validationSeq.current) return
        setValidationErrors(res.data?.errors || [])
        setValidationFailed(false)
      })
      .catch(() => {
        if (seq !== validationSeq.current) return
        // A failed check is not a clean one: "All fields valid" must not appear when
        // the server never answered.
        setValidationErrors([])
        setValidationFailed(true)
      })
      .finally(() => {
        if (seq !== validationSeq.current) return
        setValidating(false)
      })
  }, [fieldValues, headerFields, request.id, request.template_id, vendorResolved, vendorBcNumber, lineItems])

  useEffect(() => {
    // Nothing to validate while the document is still being extracted: every poll used
    // to restart this and call Business Central.
    if (showProcessingModal) return undefined
    clearTimeout(validationTimer.current)
    validationTimer.current = setTimeout(runValidation, 800)
    return () => clearTimeout(validationTimer.current)
  }, [fieldValues, lineItems, runValidation, showProcessingModal])

  // ── Server-side preflight ──
  // The server builds the real payload (vendor resolution, line expansion, BC
  // checks) and reports every blocker at once, so approval is gated on what will
  // actually happen rather than on client-side guesses alone.
  const [serverPreflight, setServerPreflight] = useState(null)
  const [preflightLoading, setPreflightLoading] = useState(true)
  const preflightTimer = useRef(null)
  // Only the newest check may write the verdict. These calls hit BC and can take
  // seconds, so without this an earlier slow reply could land after a newer one
  // and show a stale result.
  const preflightSeq = useRef(0)

  // Header values as they currently stand on screen, keyed by BC field name.
  // Sent to the preflight so its verdict reflects unsaved edits, and saved to the
  // request before posting so the server posts exactly what was reviewed.
  const buildHeaderOverrides = useCallback(() => {
    const hf = {}
    for (const f of headerFields) {
      if (f.bc_field) hf[f.bc_field] = fieldValues[f.id] ?? ''
    }
    if (vendorResolved && vendorBcNumber) hf['vendorNumber'] = vendorBcNumber
    return hf
  }, [headerFields, fieldValues, vendorResolved, vendorBcNumber])

  // Line rows as they currently stand on screen, in the key names the server's
  // line extractor understands. Blank rows and non-positive quantities are dropped
  // here so the preflight judges exactly the rows that would be posted.
  const buildLineOverrides = useCallback(() => (
    lineItems
      .filter(l => (l.lineObjectNumber || l.description) && toNumber(l.quantity, 0) > 0)
      .map(l => ({
        item_code: l.lineObjectNumber || '',
        description: l.description || '',
        quantity: toNumber(l.quantity, 0),
        unit_price: l.directUnitCost === '' || l.directUnitCost == null ? '' : toNumber(l.directUnitCost, 0),
        uom: l.unitOfMeasureCode || '',
      }))
  ), [lineItems])

  // What is written back to the stored line-items field: each edited row merged INTO
  // the row it came from, so columns the grid does not show survive. Rows added in the
  // grid have no source row and are just the reviewed values.
  const buildStoredLineRows = useCallback(() => {
    const reviewed = buildLineOverrides()
    const kept = lineItems.filter(l => (l.lineObjectNumber || l.description) && toNumber(l.quantity, 0) > 0)
    return reviewed.map((row, i) => {
      const src = kept[i]?._srcIdx
      const original = src !== undefined && rawLineRows[src] && typeof rawLineRows[src] === 'object'
        ? rawLineRows[src]
        : {}
      return { ...original, ...row }
    })
  }, [buildLineOverrides, lineItems, rawLineRows])

  const runServerPreflight = useCallback(() => {
    const seq = ++preflightSeq.current
    setPreflightLoading(true)
    bcApi.preflightDocument(request.id, buildHeaderOverrides(), buildLineOverrides())
      .then(res => {
        if (seq !== preflightSeq.current) return
        setServerPreflight(res.data || null)
      })
      .catch(err => {
        if (seq !== preflightSeq.current) return
        setServerPreflight({
          ready: false,
          blockers: [{
            check: 'preflight',
            message: err?.response?.data?.message || err?.response?.data?.error || 'Could not run pre-post checks.',
            fix: '',
          }],
          warnings: [],
          summary: {},
        })
      })
      .finally(() => {
        if (seq !== preflightSeq.current) return
        setPreflightLoading(false)
      })
  }, [request.id, buildHeaderOverrides, buildLineOverrides])

  // 1.5s of quiet before re-checking. These calls reach out to BC, so firing one
  // per keystroke made the panel thrash and the button unusable while typing.
  useEffect(() => {
    if (showProcessingModal) return undefined
    clearTimeout(preflightTimer.current)
    preflightTimer.current = setTimeout(runServerPreflight, 1500)
    return () => clearTimeout(preflightTimer.current)
  }, [fieldValues, lineItems, vendorBcNumber, runServerPreflight, showProcessingModal])

  // ── Post ──
  const [posting, setPosting] = useState(false)
  const [postResult, setPostResult] = useState(null)
  const [postAttempts, setPostAttempts] = useState(0)
  const [postProgress, setPostProgress] = useState({ percent: 0, message: '' })
  const [postMinimized, setPostMinimized] = useState(false)
  const MAX_ATTEMPTS = 3

  // The server resolves the real entity type (PO, PI, …) — never the client.
  // Until a post returns, label the document generically rather than assuming PO.
  const docLabel = postResult?.entity_display_name || 'Document'

  // The posted result, whichever way it was learned (a fresh post, a retry that found
  // the document already existed, or a reload).
  const showPosted = useCallback((d) => {
    const label = d.entity_display_name || 'Document'
    setPostResult({
      success: true,
      po_number: d.bc_document_number || d.po_number,
      entity_display_name: label,
      entity_key: d.bc_entity_type_key,
      doc_url: d.bc_document_url,
      attachments: d.attachments || { attached: [], failed: [] },
    })
    setPostAttempts(0)
  }, [])

  // A cycle of this request already posted, from the server's own ledger. Used before
  // every post and on load: the page's idea of "not posted yet" can be stale (a
  // timeout that lost the reply, a reload, another tab).
  const findPostedCycle = useCallback(async () => {
    try {
      const res = await bcApi.getDocumentHistory(request.id)
      return (res.data?.cycles || []).find(c => c.cycle_status === 'posted') || null
    } catch {
      return null
    }
  }, [request.id])

  useEffect(() => {
    let cancelled = false
    findPostedCycle().then(cycle => {
      if (cancelled || !cycle) return
      showPosted({
        bc_document_number: cycle.posted_bc_document_number,
        bc_document_url: cycle.bc_document_url,
        bc_entity_type_key: cycle.bc_entity_type_key,
        entity_display_name: bcEntityShortLabel(cycle.bc_entity_type_key).replace(/^BC /, ''),
        attachments: cycle.attachments || { attached: [], failed: [] },
      })
    })
    return () => { cancelled = true }
  }, [findPostedCycle, showPosted])

  // Persist the reviewed values FIRST. The posting pipeline builds its payload from the
  // stored request, so an unsaved edit (a just-confirmed vendor, a corrected currency)
  // would otherwise be dropped and the old value posted. Used by Post AND Retry; retry
  // skipping it re-posted the stale value that had just failed.
  async function saveReviewed() {
    if (!isEditable) return // a locked request posts exactly what is stored
    const hf = buildHeaderOverrides()
    const fieldUpdates = headerFields
      .filter(f => f.bc_field)
      .map(f => ({
        id: f.id,
        field_name: f.field_name,
        field_value: String(hf[f.bc_field] ?? ''),
      }))
    // Lines live in ONE array_of_objects field, so the reviewed grid is written back as
    // that field's JSON value. Only for purchase documents: a sales-side document derives
    // its lines from the amounts above, and overwriting them with an empty grid destroyed
    // the very values the server builds its lines from.
    if (lineItemsField && isPurchaseEntity) {
      fieldUpdates.push({
        id: lineItemsField.id,
        field_name: lineItemsField.field_name,
        field_value: JSON.stringify(buildStoredLineRows()),
      })
    }
    if (fieldUpdates.length > 0) {
      await requestsApi.updateFields(request.id, fieldUpdates, 'Reviewed before posting to Business Central')
    }
  }

  const finishTimer = useRef(null)
  useEffect(() => () => clearTimeout(finishTimer.current), [])

  async function runPost(isRetry) {
    if (posting) return
    if (isRetry && postAttempts >= MAX_ATTEMPTS) return
    setPosting(true)
    setPostResult(null)
    setPostMinimized(false)
    setPostProgress({ percent: 5, message: 'Checking for an earlier post…' })
    try {
      // The ledger decides. If a document already exists for this request, show it
      // instead of creating a second one.
      const already = await findPostedCycle()
      if (already) {
        setPosting(false)
        setPostProgress({ percent: 0, message: '' })
        showPosted({
          bc_document_number: already.posted_bc_document_number,
          bc_document_url: already.bc_document_url,
          bc_entity_type_key: already.bc_entity_type_key,
          entity_display_name: docLabel === 'Document' ? 'Document' : docLabel,
          attachments: already.attachments,
        })
        onPosted?.()
        toast.info('Already posted', `This request is already in Business Central as ${already.posted_bc_document_number || 'a document'}.`)
        return
      }

      setPostProgress({ percent: 40, message: 'Saving reviewed values…' })
      await saveReviewed()

      // Approve, then post. The pipeline refuses to post without an approved cycle:
      // that cycle is the immutable snapshot of what was approved and what the drift
      // check compares against. This button does both.
      // The reviewed lines travel with both calls. A template with no line-items field has
      // nowhere to store them, and the server builds the document from what is stored, so
      // without this the grid was shown, preflighted as postable, and then dropped.
      const lineBody = isPurchaseEntity ? { line_overrides: buildLineOverrides() } : {}
      setPostProgress({ percent: 55, message: 'Approving…' })
      await bcApi.approveDocument(request.id, lineBody)

      setPostProgress({ percent: 70, message: 'Creating document in Business Central…' })
      const res = await bcApi.postDocument(request.id, lineBody)
      const d = res.data || {}
      if (d.success) {
        const label = d.entity_display_name || 'Document'
        const docNumber = d.bc_document_number || d.po_number
        setPostProgress({ percent: 90, message: 'Finalising and saving…' })
        await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
        setPostProgress({ percent: 100, message: `${label} created!` })
        finishTimer.current = setTimeout(() => {
          setPosting(false)
          showPosted(d)
          onPosted?.()
        }, 800)
        const att = d.attachments || { attached: [], failed: [] }
        const attNote = att.attached?.length
          ? ` ${att.attached.length} file${att.attached.length === 1 ? '' : 's'} attached.`
          : ''
        toast.success(isRetry ? 'Retried successfully' : 'Posted to Business Central', `${label} ${docNumber} created successfully.${attNote}`)
        if (att.failed?.length) {
          toast.warning(
            'Some files were not attached',
            `${att.failed.length} file${att.failed.length === 1 ? '' : 's'} could not be attached to ${label} ${docNumber}. The document itself was created.`
          )
        }
      } else {
        throw Object.assign(new Error('post failed'), { response: { data: d } })
      }
    } catch (err) {
      const attempt = postAttempts + 1
      setPostAttempts(attempt)
      setPosting(false)
      setPostProgress({ percent: 0, message: '' })
      const failure = extractPostError(err?.response?.data, isRetry ? 'Retry failed' : 'Request failed')
      if (!err?.response) {
        // No answer at all (timeout, dropped connection): the post may have completed.
        failure.title = 'Business Central did not answer in time'
        failure.details.push(
          'The document may already have been created. Retry checks Business Central\'s record of this request first, so it will not create a duplicate.'
        )
      }
      setPostResult({ success: false, ...failure, attempt })
    }
  }

  const handlePost = () => runPost(false)
  const handleRetry = () => runPost(true)

  const retryAttachments = async () => {
    try {
      const res = await bcApi.retryAttachments(request.id)
      const att = res.data?.attachments || { attached: [], failed: [] }
      setPostResult(prev => (prev ? { ...prev, attachments: att } : prev))
      if (att.failed?.length) {
        toast.warning('Some files still could not be attached', `${att.failed.length} file(s) failed again.`)
      } else {
        toast.success('Files attached', 'The source documents are now on the Business Central document.')
      }
    } catch (err) {
      toast.error('Could not attach the files', describeError(err, 'Business Central rejected the request.'))
    }
  }

  const validLineCount = lineItems.filter(l => l.lineObjectNumber || l.description).length
  const hasVendorPicker = headerFields.some(f => VENDOR_FIELD_KEYS.has(f.bc_field))

  // Only a PURCHASE document has a vendor and hand-entered lines. A sales-side
  // document (a Completion Statement posts as a Sales Invoice) identifies its
  // counterparty with a customer and derives its lines from the document's own
  // amounts — so vendor confirmation and the manual line editor do not apply, and
  // demanding them produced checks that could never be satisfied.
  const bcEntityKey = request?.template_bc_entity_type || ''
  const isPurchaseEntity = !bcEntityKey || bcEntityKey.startsWith('purchase')
  // Reference fields the server could not match to a real BC record, with the
  // list to search for each. Server-declared, so no hardcoded "vendor" here.
  const unresolvedRefs = serverPreflight?.summary?.unresolved_references || []
  const unresolvedRefByKey = Object.fromEntries(unresolvedRefs.map(r => [r.field_key, r]))

  // Pre-flight checks — every gate must pass for 100% safe post
  // A line is "unmatched" only if it has content but lineObjectNumber doesn't exist in BC items
  const unmatchedLines = lineItems.filter(l => {
    if (!(l.lineObjectNumber || l.description)) return false // blank line, skip
    if (!l.lineObjectNumber) return true // has description but no item number = unmatched
    // If bcItems loaded, verify the item number actually exists
    if (bcItems.length > 0) {
      return !bcItems.some(it => it.number.toLowerCase() === l.lineObjectNumber.toLowerCase())
    }
    return false // bcItems not loaded yet, don't block
  })
  const invalidQtyLines = lineItems.filter(l => (l.lineObjectNumber || l.description) && toNumber(l.quantity, 0) <= 0)
  const missingRequiredFields = headerFields.filter(f => {
    if (VENDOR_FIELD_KEYS.has(f.bc_field)) return false // vendor handled separately
    if (BC_AUTO_FIELDS.has(f.bc_field)) return false
    return f.is_required && !String(fieldValues[f.id] ?? '').trim()
  })

  // ── Jump-to-field: scroll + pulse-highlight a field (or vendor / line items
  // section) when a warning/error referencing it is clicked. When a single
  // click target actually covers several fields (e.g. "3 required fields"),
  // repeated clicks cycle through them one at a time rather than jumping to
  // all of them at once. ──
  const fieldRowRefs = useRef({})
  const [pulsedFieldKey, setPulsedFieldKey] = useState(null)
  const fieldJumpCycleRef = useRef({})

  const resolveFieldKey = (bcFieldOrName) => {
    if (!bcFieldOrName) return null
    const f = headerFields.find(hf =>
      hf.bc_field === bcFieldOrName || hf.field_name === bcFieldOrName || hf.display_name === bcFieldOrName
    )
    return f ? `field-${f.id}` : null
  }

  const jumpToFieldKeys = (keys) => {
    const validKeys = (keys || []).filter(Boolean)
    if (validKeys.length === 0) return
    const groupId = validKeys.join('|')
    const idx = (fieldJumpCycleRef.current[groupId] || 0) % validKeys.length
    fieldJumpCycleRef.current[groupId] = idx + 1
    const key = validKeys[idx]
    const el = fieldRowRefs.current[key]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setPulsedFieldKey(key)
    setTimeout(() => setPulsedFieldKey(k => (k === key ? null : k)), 1800)
  }

  const jumpHighlightClass = (key) =>
    pulsedFieldKey === key
      ? 'rounded-lg ring-2 ring-red-400 dark:ring-red-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 animate-pulse'
      : ''

  // ── Matched / Unmatched summary items, grouped for the unified checklist card ──
  const unmatchedSummaryItems = [
    isPurchaseEntity && hasVendorPicker && !vendorResolved && { group: 'vendor', label: 'Vendor', detail: 'Not confirmed yet', keys: ['vendor'] },
    lookupsError && { group: 'header', label: 'Business Central lists', detail: 'Could not load — use Retry above', keys: [] },
    ...unresolvedRefs.map(r => ({
      group: VENDOR_FIELD_KEYS.has(r.field_key) ? 'vendor' : 'header',
      label: r.label,
      detail: r.value ? `"${r.value}" not found in Business Central` : 'No value read from the document',
      keys: [resolveFieldKey(r.field_key)].filter(Boolean),
    })),
    isPurchaseEntity && validLineCount === 0 && { group: 'lines', label: 'Line items', detail: 'No line items added', keys: ['lines'] },
    isPurchaseEntity && unmatchedLines.length > 0 && { group: 'lines', label: 'Line items', detail: unmatchedLines.length === 1 ? '1 line item not matched to BC' : `${unmatchedLines.length} line items not matched to BC`, keys: ['lines'] },
    isPurchaseEntity && invalidQtyLines.length > 0 && { group: 'lines', label: 'Line quantities', detail: invalidQtyLines.length === 1 ? '1 line has quantity ≤ 0' : `${invalidQtyLines.length} lines have quantity ≤ 0`, keys: ['lines'] },
    missingRequiredFields.length > 0 && { group: 'header', label: missingRequiredFields.length === 1 ? missingRequiredFields[0].display_name : `${missingRequiredFields.length} required fields`, detail: missingRequiredFields.length === 1 ? 'Required — still empty' : missingRequiredFields.map(f => f.display_name).join(', '), keys: missingRequiredFields.map(f => `field-${f.id}`) },
    validationErrors.length > 0 && { group: 'header', label: validationErrors.length === 1 ? (validationErrors[0].field_key || 'Validation') : `${validationErrors.length} validation errors`, detail: validationErrors.length === 1 ? validationErrors[0].message : 'See errors above', keys: validationErrors.map(e => resolveFieldKey(e.field_key || e.field)).filter(Boolean) },
  ].filter(Boolean)

  const matchedSummaryItems = [
    isPurchaseEntity && hasVendorPicker && vendorResolved && { group: 'vendor', label: 'Vendor', detail: `${vendorBcNumber} — ${vendorBcName}` },
    isPurchaseEntity && validLineCount > 0 && unmatchedLines.length === 0 && { group: 'lines', label: 'Line items', detail: `${validLineCount} line${validLineCount === 1 ? '' : 's'} matched to BC`, keys: ['lines'] },
    ...headerFields
      .filter(f => !VENDOR_FIELD_KEYS.has(f.bc_field) && !BC_AUTO_FIELDS.has(f.bc_field))
      .filter(f => {
        const val = String(fieldValues[f.id] ?? '').trim()
        if (!val) return false
        const hasErr = validationErrors.some(e => e.field_key === f.bc_field || e.field === f.bc_field)
        return !hasErr
      })
      .map(f => ({ group: 'header', label: f.display_name, detail: String(fieldValues[f.id] ?? ''), keys: [`field-${f.id}`] })),
  ].filter(Boolean)

  // Single unified checklist card, grouped Vendor / Header Fields / Line Items —
  // replaces the old separate matched/unmatched accordions.
  const CHECKLIST_GROUP_TITLES = { vendor: 'Vendor', header: 'Header Fields', lines: 'Line Items' }
  const checklistGroups = ['vendor', 'header', 'lines']
    .map(key => ({
      key,
      title: CHECKLIST_GROUP_TITLES[key],
      issues: unmatchedSummaryItems.filter(i => i.group === key),
      ok: matchedSummaryItems.filter(i => i.group === key),
    }))
    .filter(g => g.issues.length > 0 || g.ok.length > 0)

  const [showChecklist, setShowChecklist] = useState(true)
  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false)

  const preflightIssues = unmatchedSummaryItems.map(i => `${i.label}: ${i.detail}`)
  const serverBlockers = serverPreflight?.blockers || []
  const serverWarnings = serverPreflight?.warnings || []
  // Requires a green verdict from the server, but does NOT wait on an in-flight
  // re-check: the last verdict stays authoritative while a new one is on the way,
  // so the button doesn't flicker dead every time a character is typed. The post
  // itself re-validates server-side, so acting on a one-edit-old verdict is safe.
  const canPost =
    !isReader &&
    !['rejected', 'cancelled'].includes(request?.status) &&
    preflightIssues.length === 0 &&
    !posting &&
    !postResult?.success &&
    !serverPreflight?.summary?.already_posted_as &&
    serverPreflight?.ready === true

  const pdfDoc = (request?.documents || []).find(
    d => d.content_type === 'application/pdf' || d.filename?.endsWith('.pdf')
  )
  const pdfDocId = pdfDoc?.id
  const orgId = request?.organization_id || ''

  // ── Render ──
  return (
    <>
    {/* AI Processing overlay — shown when the BC request is still being extracted */}
    {showProcessingModal && (
      processingMinimized ? (
        <button
          onClick={() => setProcessingMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 pl-3 pr-4 py-2.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border border-navy-200/70 dark:border-navy-800/70 rounded-full shadow-lg shadow-navy-900/10 hover:shadow-xl hover:border-navy-300 dark:hover:border-navy-700 transition-all group"
        >
          <div className="relative w-8 h-8 flex items-center justify-center">
            <svg className="absolute inset-0 w-8 h-8 text-navy-500 animate-spin" style={{ animationDuration: '2.4s' }} viewBox="0 0 100 100" fill="none">
              <circle cx="50" cy="50" r="46" stroke="currentColor" strokeOpacity="0.15" strokeWidth="6" />
              <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeDasharray="70 220" />
            </svg>
            <div className="relative w-6 h-6 bg-gradient-to-br from-navy-500 to-navy-700 rounded-full flex items-center justify-center ring-1 ring-white/10">
              <Bot className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Extracting…</p>
            <p className="text-[10px] text-navy-600 dark:text-navy-400 font-medium tabular-nums">{processingProgress.percent}% · click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-navy-600 dark:group-hover:text-navy-300 ml-1 transition-colors" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() => setProcessingMinimized(true)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-navy-900/20 border border-navy-100 dark:border-navy-800/60 p-8 max-w-lg w-full mx-4 animate-fade-in overflow-hidden">
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-navy-500/10 blur-3xl"></div>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-navy-500/60 to-transparent"></div>
            <div className="relative flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="relative mb-1 w-20 h-20 flex items-center justify-center mx-auto">
                  <div className="absolute inset-1 rounded-full bg-navy-500/15 blur-md animate-pulse"></div>
                  <svg className="absolute inset-0 w-20 h-20 animate-spin" style={{ animationDuration: '2.4s' }} viewBox="0 0 100 100" fill="none">
                    <defs>
                      <linearGradient id="aiRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="35%" stopColor="#a855f7" />
                        <stop offset="65%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#22d3ee" />
                      </linearGradient>
                    </defs>
                    <circle cx="50" cy="50" r="46" stroke="#94a3b8" strokeOpacity="0.15" strokeWidth="3" />
                    <circle cx="50" cy="50" r="46" stroke="url(#aiRingGradient)" strokeWidth="4" strokeLinecap="round" strokeDasharray="70 220" />
                  </svg>
                  <div className="relative w-16 h-16 bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-fuchsia-500/25 ring-1 ring-white/20">
                    <Bot className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 tracking-tight">AI is Extracting Details</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-5">Hang tight — this usually only takes a moment.</p>
              <div className="w-full mb-3">
                <div className="flex items-center justify-between mb-1.5 gap-3">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate">{processingProgress.message || 'Starting…'}</span>
                  <span className="text-xs font-bold text-navy-600 dark:text-navy-400 tabular-nums shrink-0">{processingProgress.percent}%</span>
                </div>
                <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-cyan-400 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(processingProgress.percent, 2)}%` }}
                  >
                    <div className="absolute inset-0 progress-shimmer rounded-full"></div>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto text-left">
                {processingLog.length === 0 ? (
                  <div className="p-3 flex items-center gap-2 text-xs text-gray-400">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Waiting for processing to start...</span>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {processingLog.map((step, idx) => (
                      <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                        <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap mt-0.5">
                          {step.elapsed_seconds != null ? `${step.elapsed_seconds}s` : step.timestamp}
                        </span>
                        <span className={`text-xs ${
                          step.message?.includes('✅') ? 'text-green-600 dark:text-green-400 font-medium' :
                          step.message?.includes('❌') || step.message?.includes('⚠️') ? 'text-red-600 dark:text-red-400' :
                          step.message?.includes('⏳') || step.message?.includes('Polling') || step.message?.includes('waiting') ? 'text-amber-600 dark:text-amber-400' :
                          'text-gray-700 dark:text-gray-300'
                        }`}>
                          {step.message}
                        </span>
                      </div>
                    ))}
                    <div ref={el => el?.scrollIntoView({ behavior: 'smooth', block: 'end' })} />
                  </div>
                )}
              </div>
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 w-full flex items-center justify-between">
                <p className="text-[10px] text-gray-400 dark:text-gray-500">Request ID: {request?.id}</p>
                {processingLog.length > 0 && (
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">Elapsed: {processingLog[processingLog.length - 1]?.elapsed_seconds || 0}s</p>
                )}
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Click anywhere outside to minimize</p>
            </div>
          </div>
        </div>
      )
    )}
    <div className="-m-6 flex flex-col bg-gray-100 dark:bg-gray-950 overflow-hidden animate-page-in" style={{ height: 'calc(100vh - 4rem)' }}>

      <UnassignedOpCoBanner request={request} onMoved={onOpCoMoved} />
      {/* Top header bar — same gradient/dot-pattern + icon-badge + title design as the normal extraction page */}
      <div className="relative overflow-hidden bg-gradient-to-r from-navy-100/90 via-navy-50/70 to-navy-100/80 dark:from-gray-900/80 dark:via-gray-800/60 dark:to-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, var(--tw-color-navy-900, #0f2942) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-navy-600 dark:bg-navy-500"
        />
        <div
          className={`relative z-10 pl-6 pr-5 flex items-start justify-between gap-4 flex-wrap transition-[padding] duration-300 ease-out ${
            isHeaderCollapsed ? 'py-2.5' : 'pt-4 pb-3'
          }`}
        >
          <div className={`relative flex min-w-0 flex-1 basis-[280px] ${isHeaderCollapsed ? 'items-center gap-3' : 'items-start gap-3.5'}`}>
            <div
              className={`hidden sm:flex flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-600 to-navy-800 text-white shadow-md ring-1 ring-navy-900/10 transition-all ${
                isHeaderCollapsed ? 'h-9 w-9' : 'h-11 w-11'
              }`}
              title="Business Central document request"
            >
              <FileText className={isHeaderCollapsed ? 'h-4 w-4' : 'h-5 w-5'} />
            </div>
            <div className="min-w-0 flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <h1
                  className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-snug truncate"
                  title="Request title"
                >
                  {request?.title || 'Untitled Request'}
                </h1>
                {request?.template_bc_entity_type && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0"
                    title="Business Central document type this request will post as"
                  >
                    {bcEntityShortLabel(request.template_bc_entity_type)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setIsHeaderCollapsed(prev => !prev)}
                  title={isHeaderCollapsed ? 'Expand details' : 'Collapse details'}
                  className="flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 dark:hover:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isHeaderCollapsed ? '-rotate-90' : ''}`} />
                </button>
                <OpCoBadge request={request} />
                <MoveOpCoButton request={request} onMoved={onOpCoMoved} />
              </div>
              {!isHeaderCollapsed && (request?.template_name || pdfDoc?.filename) && (
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {request?.template_name && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50"
                      title="Extraction template used for this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileSpreadsheet className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {request.template_name}
                      </span>
                    </span>
                  )}
                  {pdfDoc?.filename && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50"
                      title="Source document attached to this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileText className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {pdfDoc.filename}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: AI confidence / status (top row) + action buttons (below) */}
          <div className={`flex flex-shrink-0 gap-2 ${isHeaderCollapsed ? 'flex-row items-center' : 'flex-col items-end'}`}>
            {!isHeaderCollapsed && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 shrink min-w-0 whitespace-nowrap">
                {avgConfidence != null && (
                  <span className={`group relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 cursor-default ${
                    avgConfidence >= 90
                      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                      : avgConfidence >= 70
                      ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400'
                      : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    AI Confidence: <strong>{avgConfidence}%</strong>

                    {/* Confidence legend — shown on hover, same as the normal extraction page */}
                    <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                      <div className="flex items-center gap-2.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&gt;90%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">70-89%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&lt;70%</span>
                        </div>
                      </div>
                    </div>
                  </span>
                )}
                {/* Saved timestamp — informational, stays above the action buttons */}
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 flex-shrink-0">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="text-xs whitespace-nowrap">
                    Saved {request?.updated_at ? formatTimeAgo(request.updated_at) : 'N/A'}
                  </span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-2.5 flex-wrap justify-end">
              {request?.can_cancel !== false && !postResult?.success && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold text-red-600 dark:text-red-300 bg-white dark:bg-gray-900 border border-red-300 dark:border-red-400/40 hover:bg-red-50 hover:border-red-400 dark:hover:bg-red-400/10 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.96]"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Request Modal ── */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel Request</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for cancellation <span className="text-red-500">*</span></label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Enter reason…"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason('') }}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Keep Request
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={!cancelReason.trim() || cancelling}
                className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                {cancelling ? 'Cancelling…' : 'Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div ref={mainContentRef} className={`flex flex-1 overflow-hidden ${isDraggingDivider ? 'select-none cursor-col-resize' : ''}`}>

      {/* LEFT: Review Fields — fields on the left, document on the right, matching the normal extraction page */}
      <div style={{ width: isDocViewerVisible ? `${leftPanelPct}%` : '100%' }} className={`min-w-[320px] flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 overflow-y-auto ${isDraggingDivider ? '' : 'transition-all duration-150'}`}>
        <div className="px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/60 dark:to-gray-900 pt-3.5 pb-3.5">
          <RequestPanelTabs
            tabs={[
              { key: 'fields', label: 'Fields', icon: ClipboardList },
              { key: 'activity', label: 'Activity', icon: Activity },
              { key: 'ai_ops', label: 'AI Insights', icon: Sparkles },
            ]}
            activeTab={leftPanelTab}
            onTabChange={setLeftPanelTab}
            isDocViewerVisible={isDocViewerVisible}
            onToggleDocViewer={() => { setIsDocViewerVisible(v => !v); setLeftPanelPct(50) }}
          />
          {leftPanelTab === 'fields' && (
            <FieldsPanelHeader
              fieldCount={headerFields.length}
              searchQuery={fieldSearchQuery}
              onSearchChange={setFieldSearchQuery}
              extractionFilter={fieldsExtractionFilter}
              onExtractionFilterChange={setFieldsExtractionFilter}
              extractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type)).length
              }
              notExtractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => !f.source_type || f.source_type === 'pending').length
              }
              viewFilter={fieldsViewFilter}
              onViewFilterChange={setFieldsViewFilter}
              viewIndicatorStyle={fieldsViewIndicatorStyle}
              viewTabRefs={fieldsViewTabRefs}
            />
          )}
          {leftPanelTab === 'fields' && lookupsLoading && (
            <span className="flex items-center gap-1.5 mt-2 text-xs text-gray-400" title="Loading Business Central lookups…">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Business Central lookups…
            </span>
          )}
        </div>

        {leftPanelTab === 'activity' && (
          <ActivityLogPanel logs={activityLogs} loading={loadingActivity} onRefresh={fetchActivityLogs} />
        )}

        {leftPanelTab === 'ai_ops' && (
          <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
            <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mb-3">AI Insights</h3>
            <CostBreakdown requestId={request.id} embedded />
          </div>
        )}

        {leftPanelTab === 'fields' && (
        <>
        <div className="p-4 space-y-4 flex-1">
          {/* ── Unified checklist card: "Ready to post" / "N things to fix",
              grouped by Vendor / Header Fields / Line Items, click-to-jump ── */}
          {!postResult?.success && checklistGroups.length > 0 && (() => {
            const totalIssueCount = unmatchedSummaryItems.length
            const ready = totalIssueCount === 0
            return (
              <div className={`rounded-lg border overflow-hidden ${ready ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}`}>
                <button
                  onClick={() => setShowChecklist(o => !o)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors ${
                    ready
                      ? 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100 dark:hover:bg-green-900/20'
                      : 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {ready
                      ? <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      : <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className={`text-sm font-medium ${ready ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      {ready ? 'Ready to post' : `${totalIssueCount} thing${totalIssueCount !== 1 ? 's' : ''} to fix`}
                    </span>
                  </div>
                  <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${ready ? 'text-green-400' : 'text-red-400'} ${showChecklist ? 'rotate-180' : ''}`} />
                </button>
                {showChecklist && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {checklistGroups.map(group => (
                      <div key={group.key} className="px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">{group.title}</p>
                        <div className="space-y-0.5">
                          {group.issues.map((item, i) => {
                            const clickable = item.keys && item.keys.length > 0
                            return (
                              <button
                                key={`issue-${i}`}
                                type="button"
                                onClick={() => clickable && jumpToFieldKeys(item.keys)}
                                disabled={!clickable}
                                title={clickable ? `Jump to ${item.label}` : item.label}
                                className={`w-full flex items-center justify-between gap-2 px-1 py-1.5 rounded text-left transition-colors ${
                                  clickable ? 'hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer' : 'cursor-default'
                                }`}
                              >
                                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
                                  <AlertCircle className="h-3 w-3 text-red-400 flex-shrink-0" />
                                  <span className="truncate">{item.label}</span>
                                  {clickable && <ChevronRight className="h-3 w-3 text-red-400 flex-shrink-0" />}
                                </span>
                                <span className="text-sm font-medium text-red-600 dark:text-red-400 text-right max-w-[55%] truncate" title={item.detail}>{item.detail}</span>
                              </button>
                            )
                          })}
                          {group.ok.slice(0, 6).map((item, i) => {
                            const clickable = item.keys && item.keys.length > 0
                            return (
                              <button
                                key={`ok-${i}`}
                                type="button"
                                onClick={() => clickable && jumpToFieldKeys(item.keys)}
                                disabled={!clickable}
                                title={clickable ? `Jump to ${item.label}` : item.label}
                                className={`w-full flex items-center justify-between gap-2 px-1 py-1.5 rounded text-left transition-colors ${
                                  clickable ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer' : 'cursor-default'
                                }`}
                              >
                                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
                                  <CheckCircle2 className="h-3 w-3 text-green-400 flex-shrink-0" />
                                  <span className="truncate">{item.label}</span>
                                </span>
                                <span className="text-sm text-gray-700 dark:text-gray-300 text-right max-w-[55%] truncate" title={item.detail}>{item.detail}</span>
                              </button>
                            )
                          })}
                          {group.ok.length > 6 && (
                            <p className="px-1 py-1 text-xs text-gray-400 italic">and {group.ok.length - 6} more, all confirmed.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* Deduplicated vendor section — show ONE vendor search that fills all vendor fields */}
          {(() => {
            const vendorFields = headerFields.filter(f => VENDOR_FIELD_KEYS.has(f.bc_field))
            const primaryVendorField = vendorFields[0] // use the first vendor field as the search trigger
            if (!primaryVendorField) return null
            const vConfidence = primaryVendorField.confidence || 0
            const vConfidencePercent = Math.round(vConfidence * 100)
            const vIsHighConf = vConfidence >= 0.9
            const vIsMedConf = vConfidence >= 0.7 && vConfidence < 0.9
            const vHasValue = !!(primaryVendorField.value ?? '').toString().trim()
            return (
              <div
                ref={el => { fieldRowRefs.current['vendor'] = el }}
                className={`space-y-1 p-1 -m-1 transition-shadow ${jumpHighlightClass('vendor')}`}
              >
                <label
                  onClick={() => handleViewFieldInDocument(primaryVendorField)}
                  className={`flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 ${vHasValue ? 'cursor-pointer w-fit' : ''}`}
                >
                  {showColorCoding && vHasValue && vConfidence > 0 && (
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${vIsHighConf ? 'bg-green-500' : vIsMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                  )}
                  Vendor
                  <span className="text-red-500">*</span>
                  {showConfidenceScore && vHasValue && vConfidence > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      vIsHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : vIsMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>{vConfidencePercent}%</span>
                  )}
                </label>
                {lookupsError && (
                  <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 px-3 py-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-red-700 dark:text-red-300">Business Central lists could not be loaded</p>
                      <p className="text-xs text-red-600 dark:text-red-400 break-words">{lookupsError}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setLookupsError(null); setLookupsReload(n => n + 1) }}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-300 dark:border-red-700 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20"
                    >
                      <RotateCw className="h-3 w-3" /> Retry
                    </button>
                  </div>
                )}
                <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                  <VendorField
                    extractedValue={primaryVendorField.value}
                    vendors={lookups.vendors || []}
                    resolvedNumber={vendorResolved ? vendorBcNumber : ''}
                    resolvedName={vendorResolved ? vendorBcName : ''}
                    onVendorResolved={handleVendorPicked}
                    onVendorCleared={handleVendorCleared}
                    requestId={request.id}
                    orgId={orgId}
                  />
                </EditGate>
                {vendorResolved && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">Vendor Number</p>
                      <p className="text-xs font-mono font-semibold text-gray-700 dark:text-gray-300 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">{vendorBcNumber}</p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">Vendor Name</p>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 truncate">{vendorBcName}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

          {(() => {
            let visibleFields = headerFields
            if (fieldsViewFilter === 'mandatory') {
              visibleFields = visibleFields.filter(f => f.is_required)
            } else if (fieldsViewFilter === 'custom') {
              visibleFields = visibleFields.filter(f => f.source_type === 'user_input')
            }
            if (fieldsExtractionFilter === 'extracted') {
              visibleFields = visibleFields.filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type))
            } else if (fieldsExtractionFilter === 'not_extracted') {
              visibleFields = visibleFields.filter(f => !f.source_type || f.source_type === 'pending')
            }
            if (fieldSearchQuery.trim()) {
              const q = fieldSearchQuery.toLowerCase()
              visibleFields = visibleFields.filter(f => (f.display_label || f.display_name || f.field_name || '').toLowerCase().includes(q))
            }
            return visibleFields
          })().map(field => {
            // Skip vendor fields — handled above as a single unified search
            if (VENDOR_FIELD_KEYS.has(field.bc_field)) return null
            const isAutoField = BC_AUTO_FIELDS.has(field.bc_field)
            // lookupFields is populated early (lookup-config loads before entity data)
            // so getLookupOptions() correctly drives hasLookup from the start.
            const lookupOpts = getLookupOptions(field.bc_field) || []
            const hasLookup = lookupOpts.length > 0 || !!lookupFields[field.bc_field]
            const serverErr = validationErrors.find(
              e => e.field_key === field.bc_field || e.field_key === field.field_name ||
                   e.field === field.bc_field || e.field === field.field_name
            )
            const inputType = getInputType(field)
            const val = fieldValues[field.id] ?? ''
            const isEmpty = val === '' || val === null || val === undefined

            // Hide BC-auto fields only when they have no extracted value from the backend
            // (don't hide after user clears — that causes the field to vanish mid-edit)
            const hasExtractedValue = !!(field.value ?? '')
            if (isAutoField && isEmpty && !hasExtractedValue) return null

            // Confidence helpers
            const confidence = field.confidence || 0
            const confidencePercent = Math.round(confidence * 100)
            const isLowConf = confidence > 0 && confidence < 0.7
            const isMedConf = confidence >= 0.7 && confidence < 0.9
            const isHighConf = confidence >= 0.9

            // The server checked this field against a BC master list and found no
            // match, so it needs picking from the dropdown before posting.
            const unresolvedRef = unresolvedRefByKey[field.bc_field]

            const getFieldBorderColor = () => {
              if (serverErr) return 'border-red-400 dark:border-red-600 focus:ring-red-400'
              if (unresolvedRef) return 'border-red-400 dark:border-red-600 focus:ring-red-400'
              if (field.is_required && isEmpty) return 'border-amber-400 dark:border-amber-500 focus:ring-amber-400'
              if (!showColorCoding || isEmpty) return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
              if (isLowConf) return 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 focus:ring-red-400'
              if (isMedConf) return 'border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20 focus:ring-orange-400'
              return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
            }

            const alternativeCount = field.alternative_count || 0
            const hasAlternatives = alternativeCount >= 1
            const isAlternativesOpen = openAlternativesFieldId === field.id
            const isAuditOpen = openAuditFieldId === field.id

            return (
              <div
                key={field.id}
                ref={el => { fieldRowRefs.current[`field-${field.id}`] = el }}
                onClick={() => handleViewFieldInDocument(field)}
                className={`relative space-y-1 p-1 -m-1 cursor-pointer transition-shadow ${jumpHighlightClass(`field-${field.id}`)}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 min-w-0">
                    {showColorCoding && !isEmpty && confidence > 0 && (
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHighConf ? 'bg-green-500' : isMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                    )}
                    <span className="truncate">{field.display_name}</span>
                    {field.is_required && <span className="text-red-500">*</span>}
                    {field.bc_field && (
                      <span className="font-mono text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-1 rounded">{field.bc_field}</span>
                    )}
                    {showConfidenceScore && !isEmpty && confidence > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        isHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : isMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>{confidencePercent}%</span>
                    )}
                    {showAiBadge && !isEmpty && field.source_type === 'llm_fallback' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" title="Extracted by AI (LLM fallback)">AI</span>
                    )}
                  </label>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleToggleFieldAudit(field) }}
                      className={`flex-shrink-0 p-1 rounded-md transition-all ${isAuditOpen ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'}`}
                      title="View edit history"
                    >
                      <History className="h-3.5 w-3.5" strokeWidth={isAuditOpen ? 2.75 : 2} />
                    </button>
                    {hasAlternatives && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleAlternatives(field) }}
                        className={`flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-all border ${isAlternativesOpen ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                        title={`${alternativeCount} alternative value(s) from different documents`}
                      >
                        <Layers className="h-3 w-3" />
                        <span>{alternativeCount + 1}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Lookup field — dropdown validated against BC entity */}
                {hasLookup && (
                  <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
                    <div onClick={e => e.stopPropagation()} className={unresolvedRef ? 'rounded-lg ring-1 ring-red-400 dark:ring-red-600' : ''}>
                      <LookupDropdown
                        extractedValue={field.value}
                        options={lookupOpts}
                        value={val}
                        loading={lookupsLoading && lookupOpts.length === 0}
                        onChange={(code) => setFieldValues(prev => ({ ...prev, [field.id]: code }))}
                        placeholder={`Select ${field.display_name}…`}
                      />
                    </div>
                  </EditGate>
                )}

                {/* Regular field — text/date/number input, always read-only so the
                    value stays selectable/copyable — a disabled input blocks
                    selection entirely, which is exactly what the "copy this value"
                    use case needs to avoid. */}
                {!hasLookup && (() => {
                  // A date the page cannot read is shown as text so the reviewer can fix it;
                  // a date input would silently display nothing. Numbers are text inputs with
                  // a decimal keypad: a number input blanks "1,234.50".
                  const unreadableDate = inputType === 'date' && !!val && !/^\d{4}-\d{2}-\d{2}$/.test(String(val))
                  const htmlType = unreadableDate || inputType === 'number' ? 'text' : inputType
                  return (
                    <>
                      <input
                        type={htmlType}
                        inputMode={inputType === 'number' ? 'decimal' : undefined}
                        value={val}
                        readOnly={!isEditable}
                        aria-label={field.display_name}
                        onChange={e => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        onClick={e => e.stopPropagation()}
                        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-1 cursor-text ${
                          isEditable ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/60'
                        } ${getFieldBorderColor()}`}
                      />
                      {unreadableDate && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 flex-shrink-0" /> Not a date Business Central can read — use YYYY-MM-DD
                        </p>
                      )}
                    </>
                  )
                })()}

                {/* Alternatives popup */}
                {isAlternativesOpen && (
                  <div
                    ref={alternativesPopupRef}
                    onClick={e => e.stopPropagation()}
                    className="absolute z-50 top-full right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    style={{ width: '320px' }}
                  >
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Select value <span className="text-gray-400">({alternatives.length + 1} found)</span>
                      </span>
                      <button onClick={() => setOpenAlternativesFieldId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingAlternatives ? (
                      <div className="p-6 text-center">
                        <RefreshCw className="h-4 w-4 animate-spin mx-auto text-blue-500" />
                        <span className="text-xs text-gray-500 mt-2 block">Loading...</span>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {[{ id: field.id, field_value: field.value, confidence: field.confidence, source_document_filename: field.source_document_filename, page_number: field.page_number, isActive: true }, ...alternatives.map(a => ({ ...a, isActive: false }))].map(item => (
                          <div
                            key={item.id}
                            className={`px-3 py-2.5 transition-all cursor-pointer flex items-center gap-3 ${item.isActive ? 'bg-green-50 dark:bg-green-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            onClick={() => !item.isActive && handleSelectAlternative(field, item)}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${item.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-blue-500'}`}>
                              {item.isActive && <Check className="h-2.5 w-2.5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${item.isActive ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                {item.field_value || item.extracted_value || '(empty)'}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">
                                {item.source_document_filename || 'Unknown source'}
                                {item.page_number && <span className="ml-1">• p.{item.page_number}</span>}
                              </p>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${(item.confidence || 0) >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : (item.confidence || 0) >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                              {Math.round((item.confidence || 0) * 100)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Audit history popup */}
                {isAuditOpen && (
                  <AnchoredPopover
                    ref={auditPopupRef}
                    onClick={e => e.stopPropagation()}
                    anchorClassName="top-full right-0"
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
                    style={{ width: '340px' }}
                  >
                    <div className="px-3.5 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 flex items-center justify-between">
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" /> Edit History
                      </span>
                      <button onClick={() => setOpenAuditFieldId(null)} className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-md transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingFieldAudit ? (
                      <div className="p-6 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto text-navy-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">Loading history...</span>
                      </div>
                    ) : fieldAuditLogs.length === 0 ? (
                      <div className="p-6 text-center">
                        <History className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">No edit history found</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {fieldAuditLogs.map((log, idx) => {
                          let oldValue = null, newValue = null
                          try {
                            const changes = typeof log.audit_json === 'string' ? JSON.parse(log.audit_json) : log.audit_json
                            Object.keys(changes || {}).forEach(key => {
                              if (changes[key].old !== undefined || changes[key].new !== undefined) {
                                oldValue = changes[key].old
                                newValue = changes[key].new
                              }
                            })
                          } catch { /* ignore malformed audit_json */ }
                          const actorName = log.created_by_name || log.created_by || 'System'
                          return (
                            <div key={log.id || idx} className="px-3.5 py-2.5">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{actorName}</span>
                                <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                  {log.created_at ? formatDateTime(log.created_at) : ''}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {oldValue !== null && oldValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 w-8 flex-shrink-0 pt-0.5">From</span>
                                    <span className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(oldValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                                {newValue !== null && newValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 pt-0.5">To</span>
                                    <span className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(newValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </AnchoredPopover>
                )}

                {serverErr && <p className="text-xs text-red-600 dark:text-red-400">{serverErr.message}</p>}
                {!serverErr && unresolvedRef && (
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                    {unresolvedRef.value
                      ? `“${unresolvedRef.value}” is not in Business Central — pick the matching record`
                      : `Pick the matching Business Central record`}
                  </p>
                )}
                {!serverErr && !unresolvedRef && field.is_required && isEmpty && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" /> Required
                  </p>
                )}
                {!serverErr && !isEmpty && isLowConf && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" /> Low confidence — please verify
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {/* Line Items — hand-entered only for purchase documents. A sales-side
            document derives its lines from the amounts above, so an empty item
            grid here would invite the user to add lines that are never used. */}
        {isPurchaseEntity ? (
          <div
            ref={el => { fieldRowRefs.current['lines'] = el }}
            className={`px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4 flex-shrink-0 transition-shadow ${jumpHighlightClass('lines')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Document Lines
              </p>
              {lineItems.filter(l => l.lineObjectNumber || l.description).length > 0 && (
                <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                  {lineItems.filter(l => l.lineObjectNumber || l.description).length} line{lineItems.filter(l => l.lineObjectNumber || l.description).length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <EditGate isEditMode={isEditable} onBlockedClick={notifyLocked}>
              <LineItemsTable
                lines={lineItems}
                items={bcItems}
                onChange={next => setLineItems(next)}
              />
            </EditGate>
          </div>
        ) : serverPreflight?.summary?.line_item_count > 0 && (
          <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4 flex-shrink-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Document Lines</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {serverPreflight.summary.line_item_count} line{serverPreflight.summary.line_item_count === 1 ? '' : 's'} will
              be created from the amounts above. Edit an amount to change a line.
            </p>
          </div>
        )}

        {/* ── Validate & Post section (was right panel, now at bottom of fields) ── */}
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4 flex-shrink-0 space-y-3">

          {/* Unified pre-post checklist — every gate that must clear before posting,
              grouped under one card instead of scattered same-weight boxes, so the
              reader sees one status at a glance and drills into detail below it. */}
          {!postResult?.success && (() => {
            const readyToPost = !preflightLoading && serverPreflight?.ready && preflightIssues.length === 0
            const totalIssues = preflightIssues.length + serverBlockers.length
            const headerTone = readyToPost
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : (preflightLoading && !serverPreflight)
                ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'
                : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-700'
            const visibleValidationErrors = showAllValidationErrors ? validationErrors : validationErrors.slice(0, 4)

            return (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Header: single glance at overall readiness */}
                <div className={`flex items-center gap-2 px-3.5 py-2.5 border-b ${headerTone}`}>
                  {preflightLoading && !serverPreflight ? (
                    <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />
                  ) : readyToPost ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  )}
                  <p className={`text-sm font-semibold ${
                    readyToPost ? 'text-green-700 dark:text-green-300'
                      : (preflightLoading && !serverPreflight) ? 'text-gray-600 dark:text-gray-300'
                      : 'text-amber-800 dark:text-amber-300'
                  }`}>
                    {preflightLoading && !serverPreflight
                      ? 'Checking against Business Central…'
                      : readyToPost
                        ? `Ready to post as ${serverPreflight.summary?.entity_display_name || 'document'}${serverPreflight.summary?.company_name ? ` to ${serverPreflight.summary.company_name}` : ''}`
                        : `${totalIssues} thing${totalIssues !== 1 ? 's' : ''} to fix before posting`}
                  </p>
                  {preflightLoading && serverPreflight && (
                    <span className="ml-auto text-[11px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                      <Loader2 className="h-3 w-3 animate-spin" /> re-checking
                    </span>
                  )}
                </div>

                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {/* Counterparty status. For a sales-side document this reports the
                      reference fields the server checked, not a vendor. */}
                  {!isPurchaseEntity ? (
                    <div className="flex items-start gap-2 px-3.5 py-2.5">
                      {unresolvedRefs.length === 0 ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-gray-600 dark:text-gray-300">Matched to Business Central</p>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            {unresolvedRefs.map(r => (
                              <p key={r.field_key} className="text-xs text-amber-700 dark:text-amber-300">
                                Pick the matching <span className="font-semibold">{r.label}</span> above
                                {r.value ? <> — “{r.value}” is not a Business Central record</> : null}
                              </p>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : !hasVendorPicker ? null : (
                    <div className="flex items-start gap-2 px-3.5 py-2.5">
                      {vendorResolved ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            Vendor confirmed — <span className="font-mono font-semibold">{vendorBcNumber}</span> {vendorBcName}
                          </p>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700 dark:text-amber-300">Select a vendor from the dropdown above to enable posting</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Field validation summary */}
                  {validating ? (
                    <div className="flex items-center gap-2 px-3.5 py-2.5">
                      <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin flex-shrink-0" />
                      <p className="text-xs text-gray-500">Validating fields…</p>
                    </div>
                  ) : validationFailed ? (
                    <div className="flex items-center gap-2 px-3.5 py-2.5">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                      <p className="text-xs text-amber-700 dark:text-amber-300">Couldn&apos;t validate the fields — check the connection and try again.</p>
                      <button type="button" onClick={runValidation} className="ml-auto text-xs font-medium text-navy-600 dark:text-navy-400 hover:underline">Retry</button>
                    </div>
                  ) : validationErrors.length === 0 && fields.length > 0 ? (
                    <div className="flex items-center gap-2 px-3.5 py-2.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      <p className="text-xs text-gray-600 dark:text-gray-300">All fields valid</p>
                    </div>
                  ) : null}

                  {/* Field-level validation errors, collapsed past 4 */}
                  {!validating && validationErrors.length > 0 && (
                    <div className="px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                          {validationErrors.length} field error{validationErrors.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="space-y-1 -ml-1 pl-4">
                        {visibleValidationErrors.map((err, i) => {
                          const key = resolveFieldKey(err.field_key || err.field)
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => key && jumpToFieldKeys([key])}
                              disabled={!key}
                              title={key ? `Jump to ${err.field_key || err.field}` : undefined}
                              className={`block w-full text-left text-xs text-red-600 dark:text-red-400 rounded px-1 py-0.5 transition-colors ${
                                key ? 'hover:bg-red-100/60 dark:hover:bg-red-900/20 cursor-pointer' : 'cursor-default'
                              }`}
                            >
                              <span className="font-mono font-medium">{err.field_key || err.field}</span>: {err.message}
                            </button>
                          )
                        })}
                      </div>
                      {validationErrors.length > 4 && (
                        <button
                          type="button"
                          onClick={() => setShowAllValidationErrors(v => !v)}
                          className="mt-1.5 ml-5 text-xs font-medium text-navy-600 dark:text-navy-400 hover:underline"
                        >
                          {showAllValidationErrors ? 'Show fewer' : `Show ${validationErrors.length - 4} more`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Server-side blockers — must be resolved before posting */}
                  {serverBlockers.length > 0 && (
                    <div className="px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                          {serverBlockers.length} pre-post check{serverBlockers.length !== 1 ? 's' : ''} failed
                        </p>
                      </div>
                      <div className="space-y-1.5 pl-5">
                        {serverBlockers.map((b, i) => (
                          <div key={i}>
                            <p className="text-xs text-amber-800 dark:text-amber-300 break-words">{b.message}</p>
                            {b.fix && <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 break-words">{b.fix}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Non-blocking warnings */}
                  {serverWarnings.length > 0 && (
                    <div className="px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">Notes</p>
                      </div>
                      <div className="space-y-1.5 pl-5">
                        {serverWarnings.map((w, i) => (
                          <div key={i}>
                            <p className="text-xs text-gray-600 dark:text-gray-400 break-words">{w.message}</p>
                            {w.fix && <p className="text-[11px] text-gray-400 break-words">{w.fix}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dropdown data freshness — shown only before preflight has
                      reported; once it has, its own summary covers this ground. */}
                  {!serverPreflight && masterDataInfo?.stale && (
                    <div className="flex items-start gap-2 px-3.5 py-2.5">
                      <AlertCircle className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Business Central lists were last synced {Math.round(masterDataInfo.age_hours)}h
                        ago — a vendor or item added since then won't appear here.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Post result */}
          {postResult?.success && (
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-3">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" /><p className="text-sm font-semibold text-green-700 dark:text-green-300">Posted successfully</p></div>
              <p className="text-xs text-green-600 dark:text-green-400">
                {docLabel}:{' '}
                {postResult.doc_url ? (
                  // Link built server-side, where the tenant, environment, company and
                  // entity page are known. Assembling it here produced a BC filter error.
                  <a href={postResult.doc_url} target="_blank" rel="noopener noreferrer" className="font-mono font-bold hover:underline inline-flex items-center gap-1">
                    {postResult.po_number}
                    <ExternalLink className="h-3 w-3 inline" />
                  </a>
                ) : (
                  <span className="font-mono font-bold">{postResult.po_number}</span>
                )}
              </p>
              {postResult.attachments?.attached?.length > 0 && (
                <p className="mt-1 text-xs text-green-600 dark:text-green-400 break-words">
                  Attached: {postResult.attachments.attached.join(', ')}
                </p>
              )}
              {postResult.attachments?.failed?.length > 0 && (
                <div className="mt-2 rounded border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 p-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                    {postResult.attachments.failed.length} file{postResult.attachments.failed.length === 1 ? '' : 's'} not attached to the Business Central document
                  </p>
                  <ul className="mt-1 list-disc list-inside">
                    {postResult.attachments.failed.map((f, i) => (
                      <li key={`${f.file}-${i}`} className="text-xs text-amber-700 dark:text-amber-400 break-words">
                        <span className="font-medium">{f.file || 'Attachment'}</span>{f.error ? `: ${f.error}` : ''}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={retryAttachments}
                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-xs font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/20"
                  >
                    <RotateCw className="h-3 w-3" /> Retry attachments
                  </button>
                </div>
              )}
            </div>
          )}
          {postResult && !postResult.success && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 p-3 space-y-2">
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" /><p className="text-xs font-semibold text-red-700 dark:text-red-300">Failed ({postResult.attempt}/{MAX_ATTEMPTS})</p></div>
              <p className="text-xs font-medium text-red-700 dark:text-red-300">{postResult.title || postResult.error}</p>
              {(postResult.details?.length > 0) && (
                <ul className="mt-1 mb-2 space-y-1 list-disc list-inside">
                  {postResult.details.map((d, i) => (
                    <li key={i} className="text-xs text-red-600 dark:text-red-400 break-words">{d}</li>
                  ))}
                </ul>
              )}
              {postAttempts >= MAX_ATTEMPTS
                ? <p className="text-xs text-gray-500 italic">Max attempts reached.</p>
                : <button onClick={handleRetry} disabled={posting}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-100 disabled:opacity-50">
                    {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry
                  </button>
              }
            </div>
          )}

          {/* Post button — centred at bottom */}
          <div className="flex flex-col items-center pt-2">
            <button onClick={handlePost} disabled={!canPost}
              className="w-full max-w-md flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-navy-700 hover:bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow">
              {posting
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Posting…</>
                : <><Send className="h-4 w-4" /> Approve &amp; Post {serverPreflight?.summary?.entity_display_name ? `${serverPreflight.summary.entity_display_name} ` : ''}to Business Central</>}
            </button>
            {unmatchedSummaryItems.length > 0 && !posting && !postResult?.success && (
              <p className="mt-2 text-center text-xs text-amber-600 dark:text-amber-400">
                {unmatchedSummaryItems.length} issue{unmatchedSummaryItems.length !== 1 ? 's' : ''} to resolve before posting
              </p>
            )}
            {/* Say why the button is disabled — a greyed-out control with no reason is the headache. */}
            {unmatchedSummaryItems.length === 0 && !posting && !postResult?.success && preflightLoading && !serverPreflight && (
              <p className="mt-2 text-center text-xs text-gray-500">Checking against Business Central…</p>
            )}
            {unmatchedSummaryItems.length === 0 && !posting && !postResult?.success && serverBlockers.length > 0 && (
              <p className="mt-2 text-center text-xs text-amber-600 dark:text-amber-400">
                {serverBlockers.length} pre-post check{serverBlockers.length !== 1 ? 's' : ''} failed — see above
              </p>
            )}
          </div>
        </div>
        </>
        )}

      </div>

      {/* Draggable divider — drag to resize Review Fields vs document panels */}
      <div
        onMouseDown={isDocViewerVisible ? handleDividerMouseDown : undefined}
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        className={`relative flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-teal-400 dark:hover:bg-teal-500 active:bg-teal-500 group z-10 overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isDocViewerVisible ? 'w-1 opacity-100 cursor-col-resize' : 'w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
      </div>

      {/* RIGHT: Document viewer */}
      <div
        style={{ width: isDocViewerVisible ? `${100 - leftPanelPct}%` : '0%' }}
        className={`flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isDocViewerVisible ? 'min-w-[300px] opacity-100' : 'min-w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="flex-1 bg-gray-300 dark:bg-[#2a2d30] overflow-auto p-4 flex items-start justify-center min-w-0">
          {pdfDocId
            ? <PDFViewer documentId={pdfDocId} highlightedField={highlightedField} />
            : <div className="flex items-center justify-center h-full text-sm text-gray-400">No PDF available</div>}
        </div>
      </div>

      </div>
    </div>

    {/* ── BC Posting Progress Modal (same style as AI extraction) ── */}
    {posting && (
      postMinimized ? (
        <button
          onClick={() => setPostMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group"
        >
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div className="absolute inset-0 w-8 h-8">
              <svg className="w-full h-full animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="46" fill="none" stroke="url(#bc-gradient-mini)" strokeWidth="4" strokeLinecap="round" strokeDasharray="100 200" />
                <defs>
                  <linearGradient id="bc-gradient-mini" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Posting to BC…</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{postProgress.percent}% — click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 ml-1" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setPostMinimized(true)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 opacity-10 animate-pulse" />
            <div className="relative flex flex-col items-center text-center">
              {/* Icon */}
              <div className="relative mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Bot className="h-8 w-8 text-white" />
                </div>
                <div className="absolute inset-0 w-16 h-16">
                  <svg className="w-full h-full animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="none" stroke="url(#bc-gradient)" strokeWidth="3" strokeLinecap="round" strokeDasharray="100 200" />
                    <defs>
                      <linearGradient id="bc-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="50%" stopColor="#8b5cf6" />
                        <stop offset="100%" stopColor="#6366f1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                Posting to Business Central
              </h3>

              {/* Progress bar */}
              <div className="w-full mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {postProgress.message || 'Starting…'}
                  </span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                    {postProgress.percent}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(postProgress.percent, 2)}%` }}
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="w-full space-y-2 text-left">
                {[
                  { label: 'Check for an earlier post', threshold: 5 },
                  { label: 'Save reviewed values', threshold: 40 },
                  { label: 'Approve', threshold: 55 },
                  { label: `Create ${(serverPreflight?.summary?.entity_display_name || 'document').toLowerCase()} in Business Central`, threshold: 70 },
                  { label: 'Finalise & save', threshold: 100 },
                ].map(step => {
                  const done = postProgress.percent >= step.threshold
                  const active = postProgress.percent >= step.threshold - 25 && postProgress.percent < step.threshold
                  return (
                    <div key={step.label} className="flex items-center gap-2">
                      {done
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        : active
                        ? <Loader2 className="h-3.5 w-3.5 text-indigo-500 animate-spin flex-shrink-0" />
                        : <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
                      }
                      <span className={`text-xs ${done ? 'text-green-600 dark:text-green-400 font-medium' : active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>
                        {step.label}
                      </span>
                    </div>
                  )
                })}
              </div>

              <p className="mt-4 text-[10px] text-gray-400">Click outside to minimise</p>
            </div>
          </div>
        </div>
      )
    )}
    </>
  )
}
